import { useState, useEffect, useCallback } from "react";
import { VERTICALS, VERTICAL_LIST } from "./verticals";

// In production (Vercel), requests go to /api/claude to keep the API key server-side.
// In the Claude artifacts sandbox the direct URL is used automatically.
const API = typeof window !== "undefined" && window.location.hostname !== "localhost" && !window.location.hostname.includes("claude.ai") && !window.location.hostname.includes("anthropic")
  ? "/api/claude"
  : "https://api.anthropic.com/v1/messages";
const MODEL = "claude-sonnet-4-6";

async function callClaude(prompt, system, webSearch = false, maxTokens = 4000) {
  const body = { model: MODEL, max_tokens: maxTokens, system, messages: [{ role: "user", content: prompt }] };
  if (webSearch) body.tools = [{ type: "web_search_20250305", name: "web_search" }];
  const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const data = await r.json();
  if (!r.ok) {
    const errStr = JSON.stringify(data);
    if (errStr.includes("rate_limit") || errStr.includes("exceeded_limit")) {
      let resetMsg = "";
      try {
        const inner = JSON.parse(data?.error?.message || "{}");
        const ts = inner?.resetsAt || inner?.windows?.["5h"]?.resets_at;
        if (ts) { const d = new Date(ts * 1000); const mins = Math.ceil((d - Date.now()) / 60000); resetMsg = ` Resets in ~${mins} min (${d.toLocaleTimeString()}).`; }
      } catch {}
      throw new Error(`RATE_LIMIT:API usage limit reached.${resetMsg} Please wait and retry.`);
    }
    const msg = data?.error?.message || "";
    if (errStr.includes("credit_balance") || errStr.includes("credit balance") || msg.includes("too low")) {
      throw new Error("API credits exhausted — please top up at console.anthropic.com → Plans & Billing.");
    }
    if (errStr.includes("invalid_api_key") || errStr.includes("authentication_error")) {
      throw new Error("API key error — check ANTHROPIC_API_KEY in Vercel environment variables.");
    }
    throw new Error(msg || "API request failed. Please try again.");
  }
  return data;
}

const getText = (d) => {
  if (!d?.content) return "";
  // Collect text from text blocks AND from tool_result content (web search results)
  return d.content
    .filter(b => b.type === "text")
    .map(b => b.text)
    .join("\n")
    .trim();
};

// For web search responses, Claude synthesizes in text blocks — but we need enough tokens
// for tool overhead (search queries + results consume ~1000-2000 tokens before synthesis)

const parseJSON = (text) => {
  if (!text) return null;
  let cleaned = text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
  try { return JSON.parse(cleaned); } catch {}
  const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
  if (start === -1 || end <= start) return null;
  let ex = cleaned.slice(start, end + 1).replace(/,\s*([\]}])/g, "$1");
  try { return JSON.parse(ex); } catch { return null; }
};

const fmt$ = (n) => isNaN(n) ? "—" : `$${parseFloat(n).toFixed(2)}`;
const fmtN = (n) => isNaN(n) ? "—" : parseFloat(n).toFixed(1);
const fmtK = (n) => isNaN(n) ? "—" : `$${Math.round(n).toLocaleString()}`;

const C = { bg:"#080604",card:"#12100b",card2:"#1a1710",inp:"#1e1b12",copper:"#b87333",copperL:"#d4943a",gold:"#c9a227",goldL:"#e0bb55",text:"#ecddc8",dim:"#8a7a62",dim2:"#6a5a44",border:"rgba(184,115,51,0.2)",borderH:"rgba(184,115,51,0.55)",green:"#4a7c59",greenL:"#5a9c6e",red:"#8b3030",redL:"#c05050" };
const F = { h:"Cinzel,'Palatino Linotype',serif", b:"'Crimson Text',Georgia,serif", m:"'Courier Prime','Courier New',monospace" };
const S = {
  card:(e={})=>({background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:24,...e}),
  card2:(e={})=>({background:C.card2,border:`1px solid ${C.border}`,borderRadius:10,padding:20,...e}),
  inp:(e={})=>({background:C.inp,border:`1px solid ${C.border}`,borderRadius:7,color:C.text,padding:"10px 14px",fontSize:15,fontFamily:F.b,width:"100%",boxSizing:"border-box",outline:"none",...e}),
  label:{fontSize:11,color:C.dim,fontFamily:F.h,letterSpacing:"0.14em",textTransform:"uppercase",marginBottom:5,display:"block"},
  h2:{fontFamily:F.h,color:C.copperL,fontSize:17,letterSpacing:"0.08em",margin:"0 0 16px 0"},
  h3:{fontFamily:F.h,color:C.text,fontSize:14,letterSpacing:"0.07em",margin:"0 0 10px 0"},
  divider:{height:1,background:`linear-gradient(90deg,transparent,${C.border},transparent)`,margin:"18px 0"},
  tag:(col=C.copper)=>({display:"inline-block",background:`${col}1a`,border:`1px solid ${col}44`,color:col,borderRadius:5,padding:"3px 10px",fontSize:12,fontFamily:F.h,letterSpacing:"0.05em",marginRight:6,marginBottom:6}),
};

function Divider(){return <div style={S.divider}/>;}
function Tag({label,col=C.copper}){return <span style={S.tag(col)}>{label}</span>;}
function SectionTitle({title,sub}){return(<div style={{textAlign:"center",marginBottom:28}}><div style={{display:"flex",alignItems:"center",gap:14,marginBottom:6}}><div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${C.border})`}}/><h2 style={{fontFamily:F.h,color:C.copperL,fontSize:20,letterSpacing:"0.12em",margin:0}}>{title}</h2><div style={{flex:1,height:1,background:`linear-gradient(270deg,transparent,${C.border})`}}/></div>{sub&&<p style={{color:C.dim,fontFamily:F.b,fontSize:14,margin:0,fontStyle:"italic"}}>{sub}</p>}</div>);}
function LoadingSpinner({msg}){return(<div style={{textAlign:"center",padding:"56px 20px"}}><div style={{fontSize:40,marginBottom:18}}>⚗</div><p style={{fontFamily:F.h,color:C.copperL,fontSize:15,letterSpacing:"0.1em",marginBottom:8}}>{msg}</p><p style={{color:C.dim,fontFamily:F.b,fontSize:14,marginBottom:24}}>Crafting your formula — please wait…</p><div style={{width:220,height:3,background:C.border,borderRadius:2,margin:"0 auto"}}><div style={{height:"100%",background:`linear-gradient(90deg,${C.copper},${C.gold})`,borderRadius:2,animation:"loadbar 1.6s ease-in-out infinite"}}/></div></div>);}
function EmptyState({msg}){return <div style={{textAlign:"center",padding:60,color:C.dim,fontFamily:F.b,fontSize:15}}>{msg}</div>;}

function IngTable({title,items,cols}){
  if(!items?.length)return null;
  return(<div style={{marginBottom:20}}><h3 style={S.h3}>{title}</h3><div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:13,fontFamily:F.b}}><thead><tr>{cols.map(c=><th key={c.k} style={{textAlign:"left",color:C.dim,padding:"5px 10px 8px 0",fontSize:10,fontFamily:F.h,letterSpacing:"0.12em",borderBottom:`1px solid ${C.border}`}}>{c.l}</th>)}</tr></thead><tbody>{items.map((row,i)=><tr key={i} style={{borderBottom:`1px solid ${C.border}22`}}>{cols.map(c=><td key={c.k} style={{padding:"8px 10px 8px 0",color:c.hi?C.copperL:C.text,verticalAlign:"top"}}>{row[c.k]||"—"}</td>)}</tr>)}</tbody></table></div></div>);
}

function MetricBox({label,value,col=C.gold,note}){return(<div style={{flex:1,minWidth:110,textAlign:"center",padding:"16px 12px",background:`${col}11`,border:`1px solid ${col}30`,borderRadius:9}}><div style={{color:C.dim,fontSize:10,fontFamily:F.h,letterSpacing:"0.14em",marginBottom:5}}>{label}</div><div style={{color:col,fontSize:24,fontFamily:F.h,fontWeight:700}}>{value}</div>{note&&<div style={{color:C.dim2,fontSize:11,marginTop:3}}>{note}</div>}</div>);}

function StepRow({step,accent}){
  const ac=accent||C.copper;
  return(<div style={{display:"flex",gap:16,marginBottom:22}}><div style={{width:34,height:34,borderRadius:"50%",background:ac===C.copper?`linear-gradient(135deg,${C.copper},${C.gold})`:`linear-gradient(135deg,${C.gold},${C.copperL})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#0a0704",fontFamily:F.h,fontWeight:700,fontSize:13,flexShrink:0}}>{step.stepNum}</div><div style={{flex:1}}><div style={{fontFamily:F.h,color:ac===C.copper?C.copperL:C.goldL,fontSize:13,marginBottom:5}}>{step.title}</div><p style={{color:C.text,lineHeight:1.7,fontSize:15,margin:"0 0 6px"}}>{step.detail}</p><div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:4}}>{step.timeEstimate&&<span style={{color:C.dim,fontSize:12}}>⏱ {step.timeEstimate}</span>}{step.tempTarget&&step.tempTarget!=="N/A"&&<span style={{color:C.dim,fontSize:12}}>🌡 {step.tempTarget}</span>}</div>{step.notes&&<p style={{color:C.dim,fontSize:13,fontStyle:"italic",margin:0}}>💡 {step.notes}</p>}</div></div>);
}

function PhaseBlock({phase,index}){return(<div style={S.card({marginBottom:14})}><div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}><div style={{width:24,height:24,borderRadius:"50%",background:`${C.gold}33`,border:`1px solid ${C.gold}55`,display:"flex",alignItems:"center",justifyContent:"center",color:C.gold,fontFamily:F.h,fontSize:11,fontWeight:700,flexShrink:0}}>{index+1}</div><h3 style={{...S.h3,margin:0,color:C.gold,fontSize:15}}>{phase.phaseName}</h3></div>{(phase.steps||[]).map((step,si)=><StepRow key={si} step={step} accent={C.gold}/>)}</div>);}

function MethodPanel({method}){
  return(<div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 290px",gap:18}}>
    <div>
      <div style={S.card({marginBottom:16})}><h2 style={S.h2}>{method.name}</h2><p style={{color:C.dim,lineHeight:1.65,marginBottom:20,fontSize:15}}>{method.overview}</p>{(method.steps||[]).map((step,si)=><StepRow key={si} step={step} accent={C.copper}/>)}</div>
      {method.qualityCheckpoints?.length>0&&<div style={S.card({borderColor:`${C.gold}33`})}><h3 style={{...S.h3,color:C.gold}}>Quality Checkpoints</h3>{method.qualityCheckpoints.map((q,i)=><div key={i} style={{display:"flex",gap:10,marginBottom:8}}><span style={{color:C.gold}}>◆</span><span style={{color:C.text,fontSize:14}}>{q}</span></div>)}</div>}
    </div>
    <div>
      <div style={S.card2({marginBottom:14})}><h3 style={S.h3}>Overview</h3>{[["Difficulty",method.difficulty],["Time Required",method.timeRequired],["Yield Estimate",method.yieldEstimate],["Best For",method.recommendedFor]].filter(([,v])=>v).map(([k,v])=><div key={k} style={{marginBottom:12}}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.13em"}}>{k.toUpperCase()}</div><div style={{color:C.text,fontSize:13}}>{v}</div></div>)}</div>
      <div style={S.card2({marginBottom:14})}><h3 style={S.h3}>Equipment Needed</h3>{(method.equipment||[]).map((eq,i)=><div key={i} style={{color:C.text,fontSize:13,marginBottom:6,display:"flex",gap:8}}><span style={{color:C.copper}}>▪</span>{eq}</div>)}</div>
      <div style={S.card2({marginBottom:14,borderColor:`${C.green}33`})}><h3 style={{...S.h3,color:C.greenL}}>Advantages</h3>{(method.pros||[]).map((p,i)=><div key={i} style={{color:C.text,fontSize:13,marginBottom:4}}>✓ {p}</div>)}</div>
      <div style={S.card2({borderColor:`${C.red}28`})}><h3 style={{...S.h3,color:C.redL}}>Considerations</h3>{(method.cons||[]).map((c,i)=><div key={i} style={{color:C.dim,fontSize:13,marginBottom:4}}>⚠ {c}</div>)}</div>
    </div>
  </div>);
}

function CocktailCard({cocktail,index,cardLabel}){
  const cols=[C.gold,C.copper,C.copperL];
  const col=cols[index%3];
  return(<div style={S.card({borderTop:`3px solid ${col}`,paddingTop:20})}>
    <div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.22em",marginBottom:8}}>{cardLabel||"COCKTAIL"} {String(index+1).padStart(2,"0")}</div>
    <h2 style={{fontFamily:F.h,fontSize:20,color:col,letterSpacing:"0.06em",margin:"0 0 6px"}}>{cocktail.name}</h2>
    <p style={{color:C.dim,fontStyle:"italic",marginBottom:14,lineHeight:1.55,fontSize:14}}>{cocktail.description}</p>
    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
      {cocktail.style&&<Tag label={cocktail.style} col={col}/>}
      {cocktail.difficulty&&<Tag label={cocktail.difficulty} col={cocktail.difficulty==="Easy"?C.greenL:cocktail.difficulty==="Advanced"?C.redL:C.gold}/>}
    </div>
    <Divider/>
    <h3 style={S.h3}>Ingredients</h3>
    {(cocktail.ingredients||[]).map((ing,j)=><div key={j} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border}1a`}}><span style={{color:C.text,fontSize:14}}>{ing.item}</span><div style={{textAlign:"right"}}><span style={{color:col,fontFamily:F.m,fontSize:13}}>{ing.amount}</span>{ing.notes&&<div style={{color:C.dim2,fontSize:11,fontStyle:"italic"}}>{ing.notes}</div>}</div></div>)}
    <Divider/>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
      {[["Method",cocktail.method],["Glassware",cocktail.glassware],["Ice",cocktail.ice],["Garnish",cocktail.garnish]].filter(([,v])=>v).map(([k,v])=><div key={k}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.12em"}}>{k.toUpperCase()}</div><div style={{color:C.text,fontSize:13}}>{v}</div></div>)}
    </div>
    {cocktail.flavorProfile&&<div style={{background:`${col}0e`,borderRadius:7,padding:"10px 14px",marginBottom:10}}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.12em",marginBottom:3}}>FLAVOR PROFILE</div><div style={{color:C.text,fontSize:13,fontStyle:"italic"}}>{cocktail.flavorProfile}</div></div>}
    {cocktail.occasion&&<p style={{color:C.dim2,fontSize:12,margin:0}}>🥂 Best for: {cocktail.occasion}</p>}
  </div>);
}

const TTB_CATEGORIES=[
  {value:"",label:"— Let AI suggest best category —"},
  {group:"Whisky / Whiskey"},{value:"Bourbon Whisky",label:"Bourbon Whisky"},{value:"Rye Whisky",label:"Rye Whisky"},{value:"Corn Whisky",label:"Corn Whisky"},{value:"Malt Whisky",label:"Malt Whisky"},{value:"Wheat Whisky",label:"Wheat Whisky"},{value:"Light Whisky",label:"Light Whisky"},{value:"Blended Whisky",label:"Blended Whisky"},{value:"American Whisky",label:"American Whisky (other)"},
  {group:"Gin"},{value:"Gin",label:"Gin"},{value:"American Gin",label:"American Gin"},{value:"Dry Gin",label:"Dry Gin (London Dry style)"},{value:"Geneva / Genever",label:"Geneva / Genever"},
  {group:"Rum / Cane"},{value:"Rum",label:"Rum"},{value:"Tequila",label:"Tequila"},{value:"Mezcal",label:"Mezcal"},
  {group:"Brandy"},{value:"Brandy",label:"Brandy (Grape)"},{value:"Fruit Brandy",label:"Fruit Brandy"},{value:"Applejack",label:"Applejack / Apple Brandy"},{value:"Cognac",label:"Cognac"},
  {group:"Neutral / Vodka"},{value:"Vodka",label:"Vodka"},{value:"Neutral Spirits",label:"Neutral Spirits"},
  {group:"Flavored / Specialty"},{value:"Cordial",label:"Cordial / Liqueur"},{value:"Distilled Spirits Specialty",label:"Distilled Spirits Specialty"},{value:"Bitters",label:"Bitters"},{value:"Aquavit",label:"Aquavit / Akvavit"},{value:"Absinthe",label:"Absinthe"},{value:"Flavored Whisky",label:"Flavored Whisky"},{value:"Flavored Rum",label:"Flavored Rum"},{value:"Flavored Vodka",label:"Flavored Vodka"},{value:"Flavored Gin",label:"Flavored Gin"},{value:"Flavored Brandy",label:"Flavored Brandy"},{value:"Cream Liqueur",label:"Cream Liqueur"},{value:"Cocktail",label:"Cocktail (pre-mixed)"},
];

const BASE_SPIRITS=[
  {value:"",label:"— Let AI suggest best base —"},
  {group:"Grain — Neutral"},{value:"190-proof Neutral Grain Spirit (NGS)",label:"190-Proof Neutral Grain Spirit (NGS)"},{value:"160-proof Neutral Grain Spirit",label:"160-Proof Neutral Grain Spirit"},{value:"Neutral Grain Spirit (80-proof)",label:"Neutral Grain Spirit (80-proof / Vodka base)"},
  {group:"Whiskey Distillate"},{value:"New-make Bourbon Distillate",label:"New-Make Bourbon Distillate"},{value:"New-make Rye Distillate",label:"New-Make Rye Distillate"},{value:"New-make Corn Whiskey Distillate",label:"New-Make Corn Whiskey Distillate"},{value:"New-make Malt Distillate",label:"New-Make Malt Distillate"},{value:"Aged Bourbon Whiskey",label:"Aged Bourbon Whiskey"},{value:"Aged Rye Whiskey",label:"Aged Rye Whiskey"},
  {group:"Brandy / Wine"},{value:"Grape Brandy Distillate",label:"Grape Brandy Distillate"},{value:"Apple Brandy Distillate",label:"Apple / Pear Brandy Distillate"},{value:"Neutral Grape Spirit",label:"Neutral Grape Spirit"},{value:"Wine",label:"Wine (vermouth / aperitif base)"},
  {group:"Cane"},{value:"High-proof Rum Distillate",label:"High-Proof Rum Distillate"},{value:"Light Rum Base",label:"Light Rum Base (column-distilled)"},{value:"Pot-still Rum Distillate",label:"Pot-Still Rum Distillate"},
  {group:"Agave"},{value:"Tequila Blanco",label:"Tequila Blanco"},{value:"Mezcal Distillate",label:"Mezcal Distillate"},
  {group:"Other"},{value:"Gin (re-distillation base)",label:"Gin (Re-distillation base)"},{value:"Malt Beer (for whisky distillation)",label:"Malt Beer Wash (whisky)"},{value:"Molasses Beer Wash",label:"Molasses Beer Wash (rum)"},
];

function buildOptgroups(list){
  const els=[]; let grp=null; let opts=[];
  const flush=()=>{ if(grp){els.push(<optgroup key={grp} label={grp}>{opts}</optgroup>); opts=[]; grp=null;} };
  list.forEach((item,i)=>{ if(item.group){flush(); grp=item.group;} else { const o=<option key={i} value={item.value}>{item.label}</option>; grp?opts.push(o):els.push(o); } });
  flush(); return els;
}


// ── VERTICAL SELECTOR COMPONENT ──────────────────────────────────────────────
function VerticalSelector({ onSelect, C, F }) {
  const cards = [
    { id:"spirits",  icon:"⚗",  label:"Spirits & Distilling",      tagline:"Gins, whiskies, rums, liqueurs & more",        reg:"TTB",       col:"#C9A227" },
    { id:"brewing",  icon:"🍺", label:"Craft Brewing & Cider",      tagline:"IPAs, stouts, lagers, sours, hard cider & beyond", reg:"TTB / FDA", col:"#D4943A", tab5Label:"Beer Cocktails" },
    { id:"wine",     icon:"🍷", label:"Wine & Mead",                tagline:"Wines, meads & fruit wines",           reg:"TTB",       col:"#8B3A62" },
    { id:"nonalc",   icon:"🥤", label:"Non-Alcoholic Beverages",    tagline:"Functional drinks, NA spirits, sodas & more",  reg:"FDA",       col:"#1D9E75" },
    { id:"supplements", icon:"💊", label:"Supplements & Nutraceuticals", tagline:"Capsules, powders, gummies & functional formulas", reg:"FDA / DSHEA", col:"#378ADD" },
  ];
  return (
    <div style={{maxWidth:900,margin:"0 auto",padding:"40px 20px"}}>
      <div style={{textAlign:"center",marginBottom:36}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8,justifyContent:"center"}}>
          <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,rgba(184,115,51,0.3))`,maxWidth:200}}/>
          <h2 style={{fontFamily:F.h,color:"#C9A227",fontSize:20,letterSpacing:"0.12em",margin:0}}>CHOOSE YOUR INDUSTRY</h2>
          <div style={{flex:1,height:1,background:`linear-gradient(270deg,transparent,rgba(184,115,51,0.3))`,maxWidth:200}}/>
        </div>
        <p style={{color:"#8A7A62",fontFamily:F.b,fontSize:14,margin:0,fontStyle:"italic"}}>Forge Platform covers five regulated product categories — all powered by the same AI formulation engine</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:14}}>
        {cards.map(v=>(
          <button key={v.id} onClick={()=>onSelect(v.id)}
            style={{background:"#12100b",border:`1px solid rgba(184,115,51,0.2)`,borderRadius:10,padding:"22px 20px",cursor:"pointer",textAlign:"left",transition:"all 0.2s"}}>
            <div style={{fontSize:28,marginBottom:10,filter:v.id==="spirits"?"brightness(0) invert(1) opacity(0.85)":"none"}}>{v.icon}</div>
            <div style={{fontFamily:F.h,color:v.col,fontSize:14,letterSpacing:"0.06em",marginBottom:6,fontWeight:700}}>{v.label}</div>
            <div style={{color:"#8A7A62",fontSize:12,fontFamily:F.b,lineHeight:1.4,marginBottom:10}}>{v.tagline}</div>
            <div style={{fontSize:10,color:v.col,fontFamily:F.h,letterSpacing:"0.1em",borderTop:"1px solid rgba(184,115,51,0.2)",paddingTop:8,marginTop:4}}>{v.reg}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SpiritusFargeApp() {
  const [selectedVertical, setSelectedVertical] = useState(null);
  // ── PASSWORD GATE ──
  // In production the password comes from the REACT_APP_BETA_PASSWORD env variable.
  // Falls back to "spiritus2025" if the variable is not set.
  const BETA_PASSWORD = (typeof process !== "undefined" && process.env?.REACT_APP_BETA_PASSWORD) || "spiritus2025";
  const [authed, setAuthed] = useState(() => {
    try { return sessionStorage.getItem("sf_authed") === "1"; } catch { return false; }
  });
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [mode,setMode]=useState("profile");
  const [flavorInput,setFlavorInput]=useState("");
  const [ingredientInput,setIngredientInput]=useState("");
  const [ttbCategory,setTtbCategory]=useState("");
  const [baseSpirit,setBaseSpirit]=useState("");
  const [batch,setBatch]=useState("53");
  const [batchUnit,setBatchUnit]=useState("gallons");

  const [formula,setFormula]=useState(null);
  const [safetyCheck,setSafetyCheck]=useState(null);
  const [production,setProduction]=useState(null);
  const [productionErr,setProductionErr]=useState("");
  const [debugLog,setDebugLog]=useState([]);
  const [showDebug,setShowDebug]=useState(false);
  const [market,setMarket]=useState(null);
  const [cocktails,setCocktails]=useState(null);
  const [labelData,setLabelData]=useState(null);
  const [labelBrief,setLabelBrief]=useState(null);
  const [labelBriefLoading,setLabelBriefLoading]=useState(false);
  const [labelBriefErr,setLabelBriefErr]=useState("");
  const [labelSvgErr,setLabelSvgErr]=useState("");
  const [labelSvg,setLabelSvg]=useState(null);
  const [labelSvgLoading,setLabelSvgLoading]=useState(false);
  const [labelLoading,setLabelLoading]=useState(false);
  const [labelErr,setLabelErr]=useState("");
  const [labelDistillery,setLabelDistillery]=useState("");
  const [labelCity,setLabelCity]=useState("");
  const [labelState,setLabelState]=useState("");
  const [labelVolume,setLabelVolume]=useState("750");
  const [brandImageB64,setBrandImageB64]=useState(null);
  const [brandImageType,setBrandImageType]=useState(null);
  const [brandAnalysis,setBrandAnalysis]=useState(null);
  const [brandAnalyzing,setBrandAnalyzing]=useState(false);

  const [costRows,setCostRows]=useState([]);
  const [costEstimating,setCostEstimating]=useState(false);
  const [costEstimateNote,setCostEstimateNote]=useState("");
  const [bottleCost,setBottleCost]=useState("1.50");
  const [labelCost,setLabelCost]=useState("0.35");
  const [laborH,setLaborH]=useState("8");
  const [laborR,setLaborR]=useState("25");
  const [overhead,setOverhead]=useState("15");
  const [bottleML,setBottleML]=useState("750");

  const [tab,setTab]=useState("workshop");
  const [loading,setLoading]=useState(false);
  const [status,setStatus]=useState("");
  const [err,setErr]=useState("");
  const [activeMethod,setActiveMethod]=useState(0);

  useEffect(()=>{
    const l=document.createElement("link"); l.rel="stylesheet";
    l.href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Courier+Prime:wght@400;700&display=swap";
    document.head.appendChild(l);
  },[]);

  const eco=useCallback(()=>{
    if(!formula)return null;
    const bg=parseFloat(batch)||53; const ml=parseFloat(bottleML)||750;
    const totalML=batchUnit==="gallons"?bg*3785.41:bg*1000;
    const bottles=Math.max(1,Math.floor(totalML/ml));
    const ingCost=costRows.reduce((a,r)=>a+(parseFloat(r.cost)||0),0);
    const labCost=(parseFloat(laborH)||0)*(parseFloat(laborR)||0);
    const pkgPerB=(parseFloat(bottleCost)||0)+(parseFloat(labelCost)||0);
    const cogsRaw=(ingCost+labCost)/bottles+pkgPerB;
    const cogs=cogsRaw*(1+(parseFloat(overhead)||0)/100);
    const dMSRP=formula.pricingSuggestion?.distilleryMSRP||+(cogs*2.5).toFixed(2);
    const distP=formula.pricingSuggestion?.distributorPrice||+(dMSRP*0.55).toFixed(2);
    const retP=formula.pricingSuggestion?.retailMSRP||+(distP/0.58).toFixed(2);
    const margin=dMSRP>0?((dMSRP-cogs)/dMSRP)*100:0;
    return{bottles,ingCost,labCost,pkgPerB,cogsRaw,cogs,dMSRP,distP,retP,margin};
  },[formula,batch,batchUnit,bottleML,costRows,bottleCost,labelCost,laborH,laborR,overhead]);

  const handleRateLimit=(e,setter)=>{
    if(e.message.startsWith("RATE_LIMIT:")){
      setter(e.message.replace("RATE_LIMIT:",""));
      setLoading(false); setStatus(""); return true;
    }
    return false;
  };

  const generate=async()=>{
    const hasInput=flavorInput.trim()||ingredientInput.trim();
    if(!hasInput){setErr("Please enter a flavor profile or list of ingredients.");return;}
    setErr(""); setLoading(true); setFormula(null); setSafetyCheck(null); setProduction(null); setProductionErr(""); setDebugLog([]); setMarket(null); setCocktails(null); setActiveMethod(0);

    try {
      // ── FORMULA ──
      const vertConfig = selectedVertical && selectedVertical !== "spirits" ? VERTICALS[selectedVertical] : null;
      setStatus(`${vertConfig?.icon||"⚗"} Designing your ${vertConfig?.name||"spirit"} formula…`);

      // Hard-coded FDA/TTB prohibited list — embedded in every formula prompt
      const PROHIBITED = `CRITICAL SAFETY REQUIREMENT — NEVER include any of the following prohibited ingredients under any circumstances. These are FDA-banned or TTB-prohibited substances that cannot legally appear in a commercially produced US spirit:

ABSOLUTELY PROHIBITED (FDA banned or not GRAS for beverages):
- Sassafras bark, sassafras oil, safrole, or any safrole-containing ingredient (banned FDA 21 CFR 189.180)
- Calamus root, sweet flag, or any beta-asarone-containing ingredient (FDA banned)
- Ephedra (ma huang), ephedrine, or any ephedra species (FDA banned dietary supplement)
- Comfrey (Symphytum spp.) — contains pyrrolizidine alkaloids, hepatotoxic
- Pennyroyal oil or pennyroyal extract (toxic doses of pulegone)
- Aristolochic acid or any Aristolochia species — nephrotoxic and carcinogenic
- Chaparral (Larrea tridentata) — liver and kidney toxicity
- Germander (Teucrium chamaedrys) — hepatotoxic
- Kava (Piper methysticum) — not approved as beverage alcohol additive by TTB
- Lobelia (Lobelia inflata) — toxic alkaloids
- Belladonna / Deadly Nightshade (Atropa belladonna)
- Henbane (Hyoscyamus niger)
- Mandrake (Mandragora officinarum)
- Wormwood (Artemisia absinthium) at levels causing thujone >10 ppm in finished product — if using wormwood, note thujone limits in additionalNotes
- Yohimbe or yohimbine — not approved for beverage alcohol
- Oleander (Nerium oleander) — highly toxic
- Jimsonweed / Datura (Datura stramonium) — toxic alkaloids
- Pennyroyal (Mentha pulegium) in extract or oil form
- Tonka bean / coumarin at levels exceeding FDA limits (>2 ppm coumarin in finished beverage)
- Aconite / Monkshood (Aconitum spp.) — highly toxic

If the user's requested flavor profile or ingredients would typically require any prohibited item, substitute with a safe, legal, commercially-available alternative that achieves a similar flavor result. For example: replace sassafras with legal birch or sarsaparilla; replace calamus with galangal or angelica root; replace comfrey with lemon balm or borage flower.`;

      const shortCtx = vertConfig ? `Expert ${vertConfig.expertTitle||"formulation specialist"}. Design a concise ${vertConfig.name} formula. Be brief — max 5 core ingredients, 3 secondary. Use metric amounts.` : "";
      const fPrompt = vertConfig ? `${shortCtx}
${mode==="profile"?`Concept: "${flavorInput}"`:(`Ingredients: "${ingredientInput}"`)}
${ttbCategory?`Category: "${ttbCategory}"`:""}${baseSpirit?` Base: "${baseSpirit}"`:""}
Batch: ${batch} ${batchUnit}. Never use prohibited/banned ingredients.
Return ONLY valid JSON, no markdown:
{"productName":"name","tagline":"tagline","description":"2 sentences","targetStrength":"strength","TTBCategory":"category","TTBNotes":"note","baseSpirit":"base","coreIngredients":[{"ingredient":"","amount":"","unit":"","purpose":""}],"botanicals":[],"colorings":[],"stabilizers":[],"sweeteners":[],"commercialFlavorings":[],"flavorNotes":["note1","note2"],"additionalNotes":"","pricingSuggestion":{"distilleryMSRP":45,"distributorPrice":25,"retailMSRP":38,"rationale":""}}` :
      `Expert master distiller. Design a craft spirits formula.
${mode==="profile"?`Flavor: "${flavorInput}"`:(`Ingredients: "${ingredientInput}"`)}
${ttbCategory?`TTB Category: "${ttbCategory}"`:""}${baseSpirit?` Base: "${baseSpirit}"`:""}
Batch: ${batch} ${batchUnit}. NEVER use sassafras, ephedra, comfrey, pennyroyal, calamus.

Return ONLY valid JSON, no markdown:
{"productName":"name","tagline":"tagline","description":"2 sentences","targetABV":40,"TTBCategory":"cat","TTBSubcategory":"","TTBNotes":"note","baseSpirit":"name","baseSpiritProof":190,"coreIngredients":[{"ingredient":"","amount":"","unit":"","purpose":"","source":""}],"botanicals":[{"ingredient":"","amount":"","unit":"","purpose":"","notes":""}],"colorings":[{"ingredient":"","amount":"","unit":"","type":"","notes":""}],"stabilizers":[{"ingredient":"","amount":"","unit":"","purpose":"","notes":""}],"sweeteners":[{"ingredient":"","amount":"","unit":"","purpose":"","notes":""}],"commercialFlavorings":[{"name":"","manufacturer":"","usage":"","notes":""}],"flavorNotes":["note1","note2","note3"],"colorDescription":"","additionalNotes":"","pricingSuggestion":{"distilleryMSRP":45,"distributorPrice":25,"retailMSRP":38,"rationale":""}}`;
      const fdSystem = vertConfig ? `Expert ${vertConfig.expertTitle}. Return only valid JSON, no markdown.` : "Expert master distiller and TTB compliance specialist. Return only valid JSON, no markdown. NEVER include any FDA-banned or TTB-prohibited ingredients.";
      const fd=await callClaude(fPrompt,fdSystem,false,vertConfig?2200:3000);
      const fj=parseJSON(getText(fd));
      if(!fj)throw new Error("Formula could not be parsed. Please try again.");

      // ── POST-GENERATION SCRUB: strip any danger-level ingredients that slipped through ──
      const PROHIBITED_KEYWORDS = [
        "sassafras","safrole","calamus","sweet flag","beta-asarone","ephedra","ma huang","ephedrine",
        "comfrey","symphytum","pennyroyal","pulegone","aristolochic","aristolochia","chaparral","larrea",
        "germander","teucrium","belladonna","nightshade","henbane","hyoscyamus","mandrake","mandragora",
        "lobelia","yohimbe","yohimbine","oleander","nerium","jimsonweed","datura","aconite","monkshood",
        "aconitum","tonka bean","dipteryx",
      ];
      const isBanned = (name) => {
        if (!name) return false;
        const lower = name.toLowerCase();
        return PROHIBITED_KEYWORDS.some(kw => lower.includes(kw));
      };
      const scrub = (arr) => (arr||[]).filter(item => {
        const name = item.ingredient || item.name || "";
        return !isBanned(name);
      });
      fj.coreIngredients = scrub(fj.coreIngredients);
      fj.botanicals      = scrub(fj.botanicals);
      fj.colorings       = scrub(fj.colorings);
      fj.sweeteners      = scrub(fj.sweeteners);
      fj.stabilizers     = scrub(fj.stabilizers);
      fj.commercialFlavorings = scrub(fj.commercialFlavorings);

      setFormula(fj);
      const allIng=[...(fj.coreIngredients||[]),...(fj.botanicals||[]),...(fj.sweeteners||[]),...(fj.colorings||[]),...(fj.stabilizers||[])];
      const seen=new Set();
      const rows=allIng.filter(i=>{const key=(i.ingredient||"").toLowerCase().trim();if(!key||seen.has(key))return false;seen.add(key);return true;}).slice(0,18).map(i=>({name:i.ingredient,desc:`${i.amount||""} ${i.unit||""}`.trim(),cost:""}));
      setCostRows(rows);

      // ── SAFETY CHECK ──
      setStatus("🛡 Running FDA & TTB safety screening…");
      const allIngredientNames = [
        ...(fj.coreIngredients||[]).map(i=>i.ingredient),
        ...(fj.botanicals||[]).map(i=>i.ingredient),
        ...(fj.colorings||[]).map(i=>i.ingredient),
        ...(fj.sweeteners||[]).map(i=>i.ingredient),
        ...(fj.stabilizers||[]).map(i=>i.ingredient),
        ...(fj.commercialFlavorings||[]).map(i=>i.name),
      ].filter(Boolean);

      const safetyPrompt = vertConfig ? `${vertConfig.regulatoryShort||"FDA"} regulatory specialist. Screen these ingredients for safety and compliance issues.

Ingredients to screen:
${allIngredientNames.map((n,i)=>`${i+1}. ${n}`).join("\n")}

Return ONLY valid JSON:
{"status":"clear|warning|danger","overallSummary":"1 sentence","flags":[{"ingredient":"name","severity":"danger|warning|caution","issue":"concern","recommendation":"alternative"}],"clearIngredients":["names"]}` : `TTB and FDA regulatory specialist for US craft spirits. Screen these ingredients for safety and compliance.

Ingredients:
${allIngredientNames.map((n,i)=>`${i+1}. ${n}`).join("\n")}

Return ONLY valid JSON:
{"status":"clear|warning|danger","overallSummary":"1 sentence","flags":[{"ingredient":"name","severity":"danger|warning|caution","issue":"concern","recommendation":"alternative"}],"clearIngredients":["names"]}`;

      // safety check moved to background

      // Show formula now — load rest in background
      setLoading(false); setStatus("");
      setTimeout(async () => { try { setLoading(true);

      // ── SAFETY CHECK (background) ──
      try {
        const sd2 = await callClaude(safetyPrompt, "FDA regulatory specialist. Return only valid compact JSON. Be brief.", false, 1500);
        const sj2 = parseJSON(getText(sd2));
        if (sj2) {
          // Remove danger-flagged ingredients from formula display
          const dangerIngredients = (sj2.flags||[]).filter(f=>f.severity==="danger").map(f=>f.ingredient.toLowerCase());
          if (dangerIngredients.length > 0) {
            setFormula(prev => {
              if (!prev) return prev;
              const scrub = arr => (arr||[]).filter(i => !dangerIngredients.some(d => (i.ingredient||i.name||"").toLowerCase().includes(d)));
              return {...prev, coreIngredients:scrub(prev.coreIngredients), botanicals:scrub(prev.botanicals), sweeteners:scrub(prev.sweeteners), colorings:scrub(prev.colorings), stabilizers:scrub(prev.stabilizers)};
            });
          }
          setSafetyCheck(sj2);
        } else {
          setSafetyCheck({ status: "unknown", overallSummary: "Safety check could not be parsed.", flags: [], clearIngredients: [] });
        }
      } catch(se) {
        setSafetyCheck({ status: "unknown", overallSummary: "Safety check unavailable. Verify ingredients before production.", flags: [], clearIngredients: [] });
      }

      // ── PRODUCTION ──
      const resolvedBase = vertConfig ? "" : (fj.baseSpirit||baseSpirit||"");
      const needsBaseSteps = vertConfig ? false : !!(resolvedBase&&!/neutral grain spirit|NGS|GNS/i.test(resolvedBase));
      let baseProductionData=null;
      if(needsBaseSteps){
        setStatus("🌾 Generating base spirit steps…");
        const bp=`Write a production guide for "${resolvedBase}" (${batch} ${batchUnit}, ${fj.TTBCategory}). Keep ALL string values under 12 words. Return ONLY valid JSON:
{"baseName":"${resolvedBase}","overview":"One sentence summary.","totalTimeRequired":"X days","difficulty":"Intermediate","equipment":["item1","item2","item3"],"phases":[{"phaseName":"Grain Bill and Mash","steps":[{"stepNum":1,"title":"Short title","detail":"Brief action with quantity","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"},{"stepNum":3,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"}]},{"phaseName":"Fermentation","steps":[{"stepNum":1,"title":"Short title","detail":"Brief action","timeEstimate":"X days","tempTarget":"68F","notes":"tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"X days","tempTarget":"65-72F","notes":"tip"}]},{"phaseName":"Distillation and Cuts","steps":[{"stepNum":1,"title":"Load still","detail":"Fill still check seals","timeEstimate":"1h","tempTarget":"N/A","notes":"Ensure ventilation"},{"stepNum":2,"title":"Foreshots","detail":"Discard first 50ml per gallon","timeEstimate":"15min","tempTarget":"N/A","notes":"Never consume"},{"stepNum":3,"title":"Hearts","detail":"Collect at target proof","timeEstimate":"2-4h","tempTarget":"varies","notes":"Taste regularly"},{"stepNum":4,"title":"Tails cut","detail":"Stop collect proof down","timeEstimate":"30min","tempTarget":"N/A","notes":"tip"}]}],"qualityCheckpoints":["cp1","cp2"],"safetyNotes":["note1"]}`;
        try{
          const br=await callClaude(bp,"Return only valid compact JSON. Every string under 12 words. No markdown.",false,2000);
          const raw=getText(br); setDebugLog(p=>[...p,{label:"Base Spirit Raw",text:raw.slice(0,400)}]);
          baseProductionData=parseJSON(raw); setDebugLog(p=>[...p,{label:"Base Spirit Parsed",text:baseProductionData?"OK ✓":"FAILED ✗"}]);
        }catch(e){ setDebugLog(p=>[...p,{label:"Base Spirit Error",text:e.message}]); if(handleRateLimit(e,setProductionErr))return; }
      }

      const BREWING_METHODS = ["All-Grain Brewing","Extract Brewing","Partial Mash Brewing"];
      const WINE_METHODS = ["Traditional Fermentation","Cold Stabilization Method","Kit / Concentrate Method"];
      const NONALC_METHODS = ["Cold Process Blending","Hot Fill Method","Fermentation-Based Method"];
      const SUPPLEMENT_METHODS = ["Encapsulation / Capsule Fill","Powder Blending / Stick Pack","Liquid Tincture / Softgel"];
      const VERTICAL_METHODS = {brewing:BREWING_METHODS, wine:WINE_METHODS, nonalc:NONALC_METHODS, supplements:SUPPLEMENT_METHODS};
      const METHODS = vertConfig ? (VERTICAL_METHODS[selectedVertical]||BREWING_METHODS) : (needsBaseSteps?["Cold Maceration","Hot Maceration","Compounding with Commercial Flavorings"]:["Cold Maceration","Vapor Distillation","Compounding with Commercial Flavorings"]);
      const ingList=[...(fj.coreIngredients||[]),...(fj.botanicals||[])].map(i=>i.ingredient).filter(Boolean).slice(0,6).join(", ");
      const methodResults=[];
      for(let mi=0;mi<METHODS.length;mi++){
        setStatus(`🏭 Building method ${mi+1} of 3: ${METHODS[mi]}…`);
        const mName=METHODS[mi];
        const mp=`Write a "${mName}" distillery protocol for: ${fj.productName} (${fj.TTBCategory}, ${fj.targetABV}%ABV, ${batch}${batchUnit}, base:${resolvedBase||fj.baseSpirit}, ingredients:${ingList}). Keep ALL string values under 15 words. Return ONLY valid JSON:
{"name":"${mName}","overview":"One sentence why this method fits.","difficulty":"Beginner","timeRequired":"X days","yieldEstimate":"~X bottles 750ml","equipment":["item1","item2","item3"],"steps":[{"stepNum":1,"title":"Short title","detail":"Brief action with key quantity","timeEstimate":"Xh","tempTarget":"XF or N/A","notes":"Brief tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"Brief tip"},{"stepNum":3,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"Brief tip"},{"stepNum":4,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"Brief tip"},{"stepNum":5,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"Brief tip"}],"qualityCheckpoints":["Check 1","Check 2"],"pros":["Pro 1","Pro 2"],"cons":["Con 1","Con 2"],"recommendedFor":"Beginner to intermediate producers"}`;
        try{
          const mr=await callClaude(mp,"Return only valid compact JSON. Every string under 15 words. No markdown.",false,2000);
          const rawM=getText(mr); setDebugLog(p=>[...p,{label:`Method ${mi+1} (${mName}) Raw`,text:rawM.slice(0,400)}]);
          const parsed=parseJSON(rawM); setDebugLog(p=>[...p,{label:`Method ${mi+1} Parsed`,text:parsed?.name?`OK: ${parsed.name} (${parsed.steps?.length} steps)`:"FAILED ✗"}]);
          if(parsed?.name)methodResults.push(parsed);
        }catch(e){ setDebugLog(p=>[...p,{label:`Method ${mi+1} Error`,text:e.message}]); if(handleRateLimit(e,setProductionErr))return; }
      }
      if(!methodResults.length){ setProductionErr("Production protocols could not be generated. Check debug log or use Retry."); }
      else{ setProduction({baseProduction:baseProductionData,methods:methodResults}); setActiveMethod(0); }

      // ── MARKET INTEL ──
      setStatus("🔍 Analyzing the competitive market…");
      try {
        const marketRole = vertConfig ? `You are a senior US ${vertConfig.name} market analyst.` : "You are a senior US spirits industry market analyst with encyclopedic knowledge of craft distilleries, brands, and products.";
        const mPrompt = `${marketRole}

Provide a detailed competitive market analysis for a new ${vertConfig ? vertConfig.name : "spirit"} with these characteristics:
- Category: ${vertConfig ? vertConfig.name : (fj.TTBCategory || ttbCategory || "craft spirit")}
- Flavor profile: ${flavorInput || ingredientInput}
- Target ABV: ${fj.targetABV}%
- Product description: ${fj.description || ""}
- Key botanicals/ingredients: ${[...(fj.coreIngredients||[]),...(fj.botanicals||[])].map(i=>i.ingredient).filter(Boolean).slice(0,6).join(", ")}

Include:
1. DIRECT COMPETITORS (3-4 real products): name, brand, price per 750ml, distribution, and one sentence comparison.
2. MARKET CONTEXT: 2 sentences on current trends.
3. POSITIONING: One specific recommendation on price tier and sales channel.

Use only real brands. Be concise.`;

        const md = await callClaude(mPrompt, `Senior ${vertConfig ? vertConfig.name : "spirits"} industry analyst. Be specific with real brand names.`, false, 2500);
        const marketText = getText(md);
        setMarket(marketText || "Market analysis returned empty. Please use the Retry button.");
      } catch(e) {
        setMarket("Market research unavailable. " + e.message.replace("RATE_LIMIT:", ""));
      }

      // ── COCKTAILS — one call per cocktail ──
      const cocktailLabel = selectedVertical==="wine" ? "food pairings" : (selectedVertical==="nonalc"||selectedVertical==="supplements") ? "healthy snack pairings" : "cocktail recipes";
      setStatus(`🍸 Crafting ${cocktailLabel}…`);
      const cocktailStyles = selectedVertical==="brewing" ? [
        { style: "easy casual food pairing", occasion: "taproom or casual dining" },
        { style: "elevated food pairing", occasion: "craft beer dinner or upscale restaurant" },
      ] : selectedVertical==="wine" ? [
        { style: "classic food pairing", occasion: "wine dinner or restaurant" },
        { style: "unexpected creative pairing", occasion: "special occasion or tasting" },
      ] : (selectedVertical==="nonalc"||selectedVertical==="supplements") ? [
        { style: "everyday healthy snack pairing", occasion: "daily wellness routine" },
        { style: "elevated healthy meal pairing", occasion: "special occasion or dinner" },
      ] : [
        { style: "easy approachable tasting room serve", occasion: "distillery tasting room or casual entertaining" },
        { style: "sophisticated on-trend bartender-driven cocktail", occasion: "craft cocktail bar or upscale restaurant" },
      ];
      const cocktailResults = [];
      for (let ci = 0; ci < cocktailStyles.length; ci++) {
        const itemLabel = selectedVertical==="wine"?"pairing":selectedVertical==="nonalc"||selectedVertical==="supplements"?"pairing":"cocktail";
        setStatus(`🍸 Creating ${itemLabel} ${ci + 1} of 2…`);
        const cs = cocktailStyles[ci];
        const isWine = selectedVertical==="wine";
        const isWellness = selectedVertical==="nonalc"||selectedVertical==="supplements";
        const cpIntro = isWine
          ? `Create one food pairing recommendation for: ${fj.productName||"this wine"} (${(fj.flavorNotes||[]).slice(0,3).join(", ")||flavorInput}). Occasion: ${cs.occasion}.`
          : isWellness
          ? `Create one healthy snack or meal pairing for: ${fj.productName||"this product"} (${(fj.flavorNotes||[]).slice(0,3).join(", ")||flavorInput}). Occasion: ${cs.occasion}.`
          : `Create one ${cs.style} cocktail featuring: ${fj.productName||"this spirit"} (${(fj.flavorNotes||[]).slice(0,3).join(", ")||flavorInput}, ${fj.targetABV}%ABV). Best for: ${cs.occasion}.`;
        const cp = `${cpIntro}
Keep ALL string values concise. Return ONLY valid JSON — one object, not an array:
{"name":"name","description":"One sentence.","style":"${cs.style.split(",")[0]}","ingredients":[{"item":"ingredient","amount":"amount","notes":""}],"method":"preparation","ice":"","glassware":"","garnish":"","flavorProfile":"Brief description.","difficulty":"Easy","occasion":"${cs.occasion}"}`;
        try {
          const cd = await callClaude(cp, "Expert cocktail consultant. Return only one valid JSON object, no array wrapper, no markdown.", false, 1000);
          const raw = getText(cd);
          // Handle both {"name":...} and {"cocktails":[...]} shapes
          let parsed = parseJSON(raw);
          if (parsed?.cocktails?.length) parsed = parsed.cocktails[0];
          if (parsed?.name) cocktailResults.push(parsed);
        } catch(e) {
          if (handleRateLimit(e, () => {})) break;
        }
      }
      if (cocktailResults.length > 0) {
        setCocktails({ cocktails: cocktailResults });
      } else {
        setCocktails({ cocktails: [], error: "Cocktail recipes could not be generated. Use the Retry button." });
      }

      setStatus("");
      } catch(bgErr) { console.error(bgErr); } finally { setLoading(false); setStatus(""); }
      }, 100);
    }catch(e){
      if(!handleRateLimit(e,setErr)) setErr(`Error: ${e.message}`);
    }finally{ setLoading(false); }
  };

  const retrySafetyCheck = async () => {
    if (!formula) return;
    setSafetyCheck({ status:"unknown", overallSummary:"Retrying safety check…", flags:[], clearIngredients:[] });
    try {
      const allIng = [
        ...(formula.coreIngredients||[]),
        ...(formula.botanicals||[]),
        ...(formula.colorings||[]),
        ...(formula.sweeteners||[]),
        ...(formula.stabilizers||[]),
        ...(formula.commercialFlavorings||[])
      ].map(i=>i.ingredient||i.name).filter(Boolean);
      const ingList = allIng.map((n,i)=>`${i+1}. ${n}`).join("\n");
      const sp = activeVertConfig
        ? `${activeVertConfig.regulatoryShort||"FDA"} regulatory specialist. Screen these ingredients for safety and compliance issues.\n\nIngredients:\n${ingList}\n\nReturn ONLY valid JSON:\n{"status":"clear|warning|danger","overallSummary":"1 sentence","flags":[{"ingredient":"name","severity":"danger|warning|caution","issue":"concern","recommendation":"alternative"}],"clearIngredients":["names"]}`
        : `TTB and FDA regulatory specialist for US craft spirits. Screen these ingredients for safety and compliance.\n\nIngredients:\n${ingList}\n\nReturn ONLY valid JSON:\n{"status":"clear|warning|danger","overallSummary":"1 sentence","flags":[{"ingredient":"name","severity":"danger|warning|caution","issue":"concern","recommendation":"alternative"}],"clearIngredients":["names"]}`;
      const sd = await callClaude(sp, "FDA regulatory specialist. Return only valid compact JSON. Be brief.", false, 1500);
      const sj = parseJSON(getText(sd));
      setSafetyCheck(sj || {status:"unknown", overallSummary:"Safety check could not be parsed.", flags:[], clearIngredients:[]});
    } catch(e) {
      setSafetyCheck({status:"unknown", overallSummary:"Safety check unavailable. Verify ingredients before production.", flags:[], clearIngredients:[]});
    }
  };

  const retryProduction=async()=>{
    if(!formula)return;
    setProductionErr(""); setProduction(null); setActiveMethod(0); setDebugLog([]); setLoading(true);
    try{
      const fj=formula;
      const resolvedBase=fj.baseSpirit||baseSpirit||"";
      const needsBaseSteps=!!(resolvedBase&&!/neutral grain spirit|NGS|GNS/i.test(resolvedBase));
      let baseProductionData=null;
      if(needsBaseSteps){
        setStatus("🌾 Retrying base spirit steps…");
        const bp=`Write a production guide for "${resolvedBase}" (${batch} ${batchUnit}, ${fj.TTBCategory}). Keep ALL strings under 12 words. Return ONLY valid JSON:
{"baseName":"${resolvedBase}","overview":"One sentence summary.","totalTimeRequired":"X days","difficulty":"Intermediate","equipment":["item1","item2","item3"],"phases":[{"phaseName":"Grain Bill and Mash","steps":[{"stepNum":1,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"},{"stepNum":3,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"XF","notes":"tip"}]},{"phaseName":"Fermentation","steps":[{"stepNum":1,"title":"Short title","detail":"Brief action","timeEstimate":"X days","tempTarget":"68F","notes":"tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"X days","tempTarget":"N/A","notes":"tip"}]},{"phaseName":"Distillation and Cuts","steps":[{"stepNum":1,"title":"Load still","detail":"Fill still check seals","timeEstimate":"1h","tempTarget":"N/A","notes":"Ensure ventilation"},{"stepNum":2,"title":"Foreshots","detail":"Discard 50ml per gallon","timeEstimate":"15min","tempTarget":"N/A","notes":"Never consume"},{"stepNum":3,"title":"Hearts","detail":"Collect at target proof","timeEstimate":"2-4h","tempTarget":"varies","notes":"Taste regularly"},{"stepNum":4,"title":"Tails cut","detail":"Stop collect proof down","timeEstimate":"30min","tempTarget":"N/A","notes":"tip"}]}],"qualityCheckpoints":["cp1","cp2"],"safetyNotes":["note1"]}`;
        try{
          const br=await callClaude(bp,"Return only valid compact JSON. Every string under 12 words. No markdown.",false,2000);
          baseProductionData=parseJSON(getText(br));
        }catch(e){ if(handleRateLimit(e,setProductionErr))return; }
      }
      const METHODS=["Cold Maceration","Hot Maceration","Compounding with Commercial Flavorings"];
      const ingList=[...(fj.coreIngredients||[]),...(fj.botanicals||[])].map(i=>i.ingredient).filter(Boolean).slice(0,5).join(", ");
      const methodResults=[];
      for(let mi=0;mi<METHODS.length;mi++){
        setStatus(`🏭 Retrying method ${mi+1} of 3…`);
        const mName=METHODS[mi];
        const mp=`Write a "${mName}" protocol for: ${fj.productName} (${fj.TTBCategory}, ${batch} ${batchUnit}, ingredients: ${ingList}). Keep ALL strings under 12 words. Return ONLY valid JSON:
{"name":"${mName}","overview":"One sentence why this fits.","difficulty":"Beginner","timeRequired":"X days","yieldEstimate":"~X bottles 750ml","equipment":["item1","item2","item3"],"steps":[{"stepNum":1,"title":"Short title","detail":"Brief action with quantity","timeEstimate":"Xh","tempTarget":"N/A","notes":"tip"},{"stepNum":2,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"tip"},{"stepNum":3,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"tip"},{"stepNum":4,"title":"Short title","detail":"Brief action","timeEstimate":"Xh","tempTarget":"N/A","notes":"tip"}],"qualityCheckpoints":["cp1","cp2"],"pros":["pro1","pro2"],"cons":["con1","con2"],"recommendedFor":"Beginner to intermediate producers"}`;
        try{
          const mr=await callClaude(mp,"Return only valid compact JSON. Every string under 12 words. No markdown.",false,2000);
          const parsed=parseJSON(getText(mr));
          if(parsed?.name)methodResults.push(parsed);
        }catch(e){ if(handleRateLimit(e,setProductionErr))return; }
      }
      if(!methodResults.length) setProductionErr("Still unable to generate. Try regenerating the full formula.");
      else{ setProduction({baseProduction:baseProductionData,methods:methodResults}); setActiveMethod(0); }
    }catch(e){ if(!handleRateLimit(e,setProductionErr)) setProductionErr(`Retry failed: ${e.message}`); }
    finally{ setLoading(false); setStatus(""); }
  };

  const estimateCosts = async () => {
    if (!formula || !costRows.length) return;
    setCostEstimating(true);
    setCostEstimateNote("");
    try {
      const bg = parseFloat(batch) || 53;
      const unit = batchUnit;

      // Build ingredient list with amounts for pricing context
      const allIngredients = [
        ...(formula.coreIngredients||[]).map(i=>({ name:i.ingredient, amount:i.amount, unit:i.unit, category:"base spirit or core ingredient" })),
        ...(formula.botanicals||[]).map(i=>({ name:i.ingredient, amount:i.amount, unit:i.unit, category:"botanical/herb" })),
        ...(formula.sweeteners||[]).map(i=>({ name:i.ingredient, amount:i.amount, unit:i.unit, category:"sweetener" })),
        ...(formula.colorings||[]).map(i=>({ name:i.ingredient, amount:i.amount, unit:i.unit, category:"coloring" })),
        ...(formula.stabilizers||[]).map(i=>({ name:i.ingredient, amount:i.amount, unit:i.unit, category:"stabilizer" })),
      ].filter(i=>i.name).slice(0,18);

      const vertLabel = activeVertConfig ? activeVertConfig.name : "craft distillery";
      const vertCategory = formula.TTBCategory || formula.targetStrength || vertLabel;
      const prompt = `You are a ${vertLabel} procurement specialist with deep knowledge of US ingredient and raw material pricing.

Estimate the realistic TOTAL BATCH COST in USD for each ingredient below, for a ${bg} ${unit} production batch of "${formula.productName}" (${vertCategory}).

Ingredients:
${allIngredients.map((i,idx)=>`${idx+1}. ${i.name} — ${i.amount||"?"} ${i.unit||""} per batch (category: ${i.category})`).join("\n")}

For each ingredient:
- Estimate the realistic total cost for the FULL ${bg} ${unit} batch
- Base prices on typical US craft distillery wholesale/commercial supplier pricing (not retail)
- For base spirits (NGS, rum distillate, etc.) use bulk gallon pricing
- For botanicals use typical small-quantity commercial supplier pricing (Bulk Apothecary, Penn Herb, etc.)
- For commercial sweeteners/colorings use food-grade commercial pricing

Return ONLY valid JSON, no markdown:
{"estimates":[{"name":"name","batchCost":0.00,"unitNote":"price×qty","confidence":"high/medium/low"}],"disclaimer":"one sentence"}`;

      const resp = await callClaude(prompt, `Expert ${activeVertConfig ? activeVertConfig.name : "distillery"} procurement specialist. Return only valid compact JSON, no markdown.`, false, 2500);
      const raw = getText(resp);
      const parsed = parseJSON(raw);

      if (parsed?.estimates?.length) {
        setCostRows(prev => prev.map(row => {
          const match = parsed.estimates.find(e =>
            e.name.toLowerCase().includes(row.name.toLowerCase().split(" ")[0]) ||
            row.name.toLowerCase().includes(e.name.toLowerCase().split(" ")[0])
          );
          if (match && match.batchCost > 0) {
            return { ...row, cost: match.batchCost.toFixed(2), estimated: true, unitNote: match.unitNote || "", confidence: match.confidence || "medium" };
          }
          return row;
        }));
        setCostEstimateNote(parsed.disclaimer || "Estimates based on typical US craft distillery wholesale pricing. Update with your actual supplier costs for accurate COGS.");
      } else {
        setCostEstimateNote("Could not parse cost estimates. Please enter costs manually.");
      }
    } catch(e) {
      setCostEstimateNote("Cost estimation failed: " + e.message.replace("RATE_LIMIT:","Usage limit reached — try again later."));
    } finally {
      setCostEstimating(false);
    }
  };

  const exportPDF = () => {
    if (!formula) return;
    const ed = eco();
    const date = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });

    const tbl = (rows, cols) => {
      if (!rows?.length) return "";
      return `<table><thead><tr>${cols.map(c=>`<th>${c.l}</th>`).join("")}</tr></thead><tbody>${rows.map(r=>`<tr>${cols.map(c=>`<td>${r[c.k]||"—"}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    };

    const ingSection = (title, rows, cols) => rows?.length ? `<div class="section"><h3>${title}</h3>${tbl(rows,cols)}</div>` : "";

    const productionHTML = production?.methods?.[0] ? (() => {
      const m = production.methods[0];
      return `<div class="section"><h2>Production Method: ${m.name}</h2>
        <p class="sub">${m.overview}</p>
        <div class="meta-row"><span><b>Difficulty:</b> ${m.difficulty||"—"}</span><span><b>Time:</b> ${m.timeRequired||"—"}</span><span><b>Yield:</b> ${m.yieldEstimate||"—"}</span></div>
        <div class="steps">${(m.steps||[]).map(s=>`<div class="step"><div class="step-num">${s.stepNum}</div><div class="step-body"><div class="step-title">${s.title}</div><p>${s.detail}</p>${s.timeEstimate?`<span class="tag">⏱ ${s.timeEstimate}</span>`:""}${s.tempTarget&&s.tempTarget!=="N/A"?`<span class="tag">🌡 ${s.tempTarget}</span>`:""}<p class="note">${s.notes||""}</p></div></div>`).join("")}</div>
        ${m.qualityCheckpoints?.length?`<h4>Quality Checkpoints</h4><ul>${m.qualityCheckpoints.map(q=>`<li>${q}</li>`).join("")}</ul>`:""}
      </div>`;
    })() : "";

    const baseHTML = production?.baseProduction?.phases?.length ? (() => {
      const bp = production.baseProduction;
      return `<div class="section phase1-banner"><h2>Phase 1 — Base Spirit Production: ${bp.baseName}</h2>
        <p class="sub">${bp.overview||""}</p>
        ${bp.phases.map(ph=>`<h4>${ph.phaseName}</h4><div class="steps">${(ph.steps||[]).map(s=>`<div class="step"><div class="step-num gold">${s.stepNum}</div><div class="step-body"><div class="step-title">${s.title}</div><p>${s.detail}</p>${s.timeEstimate?`<span class="tag">⏱ ${s.timeEstimate}</span>`:""}${s.tempTarget&&s.tempTarget!=="N/A"?`<span class="tag">🌡 ${s.tempTarget}</span>`:""}<p class="note">${s.notes||""}</p></div></div>`).join("")}</div>`).join("")}
        ${bp.safetyNotes?.length?`<div class="safety"><b>⚠ Safety:</b> ${bp.safetyNotes.join(" · ")}</div>`:""}
      </div>`;
    })() : "";

    const ecoHTML = ed ? `<div class="section"><h2>Economics & Pricing</h2>
      <div class="eco-grid">
        <div class="eco-card"><div class="eco-label">Bottles from Batch</div><div class="eco-val">${ed.bottles.toLocaleString()}</div></div>
        <div class="eco-card"><div class="eco-label">COGS / Bottle</div><div class="eco-val">${fmt$(ed.cogs)}</div></div>
        <div class="eco-card gold"><div class="eco-label">Gross Margin</div><div class="eco-val">${fmtN(ed.margin)}%</div></div>
        <div class="eco-card"><div class="eco-label">Distillery MSRP</div><div class="eco-val">${fmt$(ed.dMSRP)}</div></div>
        <div class="eco-card"><div class="eco-label">Distributor Price</div><div class="eco-val">${fmt$(ed.distP)}</div></div>
        <div class="eco-card"><div class="eco-label">Retail MSRP</div><div class="eco-val">${fmt$(ed.retP)}</div></div>
      </div>
      ${(() => { const dedupedRows = costRows.filter(r=>r.cost).filter((r,i,arr)=>arr.findIndex(x=>x.name.toLowerCase()===r.name.toLowerCase())===i); const hasUnit = dedupedRows.some(r=>r.unitNote); const total = dedupedRows.reduce((a,r)=>a+(parseFloat(r.cost)||0),0); return dedupedRows.length ? `<h4>Ingredient Cost Breakdown</h4><table><thead><tr><th>Ingredient</th><th>Qty / Batch</th><th>Batch Cost</th>${hasUnit?"<th>Unit Pricing</th>":""}</tr></thead><tbody>${dedupedRows.map(r=>`<tr><td>${r.name}${r.estimated?` <span class="est-badge">EST</span>`:""}</td><td>${r.desc}</td><td>$${parseFloat(r.cost).toFixed(2)}</td>${hasUnit?`<td>${r.unitNote||"—"}</td>`:""}</tr>`).join("")}</tbody><tfoot><tr><td colspan="${hasUnit?3:2}"><b>Total</b></td><td><b>$${total.toFixed(2)}</b></td></tr></tfoot></table>` : ""; })()}
      ${costEstimateNote?`<p class="note">${costEstimateNote}</p>`:""}
    </div>` : "";

    const cocktailsHTML = cocktails?.cocktails?.length ? `<div class="section"><h2>Signature Cocktails</h2>
      ${cocktails.cocktails.map(c=>`<div class="cocktail-card"><h3>${c.name}</h3><p class="sub">${c.description}</p>
        <div class="meta-row"><span><b>Method:</b> ${c.method||"—"}</span><span><b>Glass:</b> ${c.glassware||"—"}</span><span><b>Ice:</b> ${c.ice||"—"}</span><span><b>Garnish:</b> ${c.garnish||"—"}</span></div>
        <table><thead><tr><th>Ingredient</th><th>Amount</th></tr></thead><tbody>${(c.ingredients||[]).map(i=>`<tr><td>${i.item}</td><td>${i.amount}${i.notes?` <span class="note">(${i.notes})</span>`:""}</td></tr>`).join("")}</tbody></table>
        ${c.flavorProfile?`<p class="flavor"><i>"${c.flavorProfile}"</i></p>`:""}
      </div>`).join("")}
    </div>` : "";

    const renderMarkdown = (text) => {
      if (!text) return "";
      return text
        .replace(/^#{1,2}\s+(.+)$/gm, (_, t) => `</p><h3 class="mkt-h3">${t}</h3><p>`)
        .replace(/^#{3,}\s+(.+)$/gm, (_, t) => `</p><h4 class="mkt-h4">${t}</h4><p>`)
        .replace(/^---+$/gm, `</p><hr class="mkt-rule"><p>`)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/^[-•]\s+(.+)$/gm, "<li>$1</li>")
        .replace(/(<li>.*<\/li>\n?)+/g, (m) => `</p><ul>${m}</ul><p>`)
        .replace(/\n\n+/g, "</p><p>")
        .replace(/\n/g, "<br>")
        .replace(/^/, "<p>")
        .replace(/$/, "</p>")
        .replace(/<p><\/p>/g, "")
        .replace(/<p>(<h[34])/g, "$1")
        .replace(/(<\/h[34]>)<\/p>/g, "$1");
    };
    const marketHTML = market && !market.includes("unavailable") ? `<div class="section"><h2>Market Intelligence</h2><div class="market-body">${renderMarkdown(market)}</div></div>` : "";

    const safetyHTML = safetyCheck ? (() => {
      const sc = safetyCheck;
      if (sc.status === "clear") {
        return `<div class="section safety-clear"><h2>✅ Regulatory Safety Check — All Clear</h2><p>${sc.overallSummary}</p>${sc.clearIngredients?.length?`<p><b>Cleared ingredients:</b> ${sc.clearIngredients.join(", ")}</p>`:""}<p class="safety-disclaimer">This AI-assisted screening is not a substitute for professional TTB compliance review. Verify with a licensed specialist before commercial production.</p></div>`;
      }
      if (!sc.flags?.length && sc.status !== "danger") return "";
      const flagHTML = (sc.flags||[]).map(f=>{
        const cls = f.severity==="danger"?"flag-danger":f.severity==="warning"?"flag-warning":"flag-caution";
        return `<div class="flag ${cls}"><div class="flag-head"><b>${f.ingredient}</b> <span class="flag-badge">${f.severity.toUpperCase()}</span>${f.regulation?` <span class="flag-ref">Ref: ${f.regulation}</span>`:""}</div><p>${f.issue}</p>${f.recommendation?`<p class="flag-rec">→ ${f.recommendation}</p>`:""}</div>`;
      }).join("");
      return `<div class="section safety-alert"><h2>${sc.status==="danger"?"🚫 DANGER — Prohibited Ingredients":"⚠ CAUTION — Regulatory Flags Require Review"}</h2><p>${sc.overallSummary}</p>${flagHTML}<p class="safety-disclaimer">This AI-assisted screening is not a substitute for professional TTB compliance review. Verify with a licensed specialist before commercial production.</p></div>`;
    })() : "";

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Spiritus Forge — ${formula.productName}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
  :root { --copper:#b87333; --gold:#c9a227; --dark:#1a0e05; --mid:#2e1f0d; --text:#1a0e05; --dim:#6b4c2a; --green:#2d5a3d; }
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Crimson Text',Georgia,serif; font-size:13pt; color:var(--text); background:#fff; }
  @page { margin:18mm 16mm; size:letter; }
  @media print { .no-print{display:none} body{-webkit-print-color-adjust:exact;print-color-adjust:exact;} }

  /* ── COVER ── */
  .cover { page-break-after:always; min-height:100vh; display:flex; flex-direction:column; justify-content:center; align-items:center; text-align:center; background:linear-gradient(160deg,#1a0e05 0%,#2e1a08 50%,#1a0e05 100%); padding:60px 40px; color:#ecddc8; }
  .cover-eyebrow { font-family:'Cinzel',serif; font-size:8pt; letter-spacing:.5em; color:var(--copper); margin-bottom:24px; text-transform:uppercase; }
  .cover-title { font-family:'Cinzel',serif; font-size:42pt; font-weight:700; letter-spacing:.12em; background:linear-gradient(135deg,var(--copper),var(--gold)); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; margin-bottom:12px; line-height:1.1; }
  .cover-tagline { font-size:16pt; font-style:italic; color:#c9a88a; margin-bottom:36px; }
  .cover-divider { width:180px; height:1px; background:linear-gradient(90deg,transparent,var(--copper),transparent); margin:0 auto 36px; }
  .cover-meta { display:grid; grid-template-columns:1fr 1fr 1fr; gap:20px; max-width:500px; margin-bottom:36px; }
  .cover-meta-item { border:1px solid rgba(184,115,51,.35); border-radius:8px; padding:14px 10px; text-align:center; }
  .cover-meta-label { font-family:'Cinzel',serif; font-size:7pt; letter-spacing:.2em; color:var(--copper); text-transform:uppercase; margin-bottom:6px; }
  .cover-meta-val { font-family:'Cinzel',serif; font-size:16pt; font-weight:700; color:var(--gold); }
  .cover-ttb { margin-top:8px; }
  .cover-ttb span { display:inline-block; background:rgba(184,115,51,.15); border:1px solid rgba(184,115,51,.4); color:var(--copper); border-radius:5px; padding:4px 14px; font-family:'Cinzel',serif; font-size:8pt; letter-spacing:.08em; margin:4px; }
  .cover-notes { font-size:11pt; color:#a08060; font-style:italic; margin-top:12px; max-width:480px; line-height:1.6; }
  .cover-date { font-family:'Cinzel',serif; font-size:8pt; letter-spacing:.18em; color:#7a5a3a; margin-top:32px; }
  .flavor-tags { margin-top:16px; display:flex; gap:8px; flex-wrap:wrap; justify-content:center; }
  .flavor-tag { background:rgba(201,162,39,.12); border:1px solid rgba(201,162,39,.35); color:#c9a227; border-radius:20px; padding:4px 14px; font-size:9pt; }

  /* ── SECTIONS ── */
  .section { margin-bottom:24px; page-break-inside:avoid; }
  .section:not(.cover) { padding-top:4px; }
  h2 { font-family:'Cinzel',serif; font-size:14pt; font-weight:600; color:var(--copper); letter-spacing:.06em; margin:0 0 10px; padding-bottom:6px; border-bottom:1px solid rgba(184,115,51,.3); }
  h3 { font-family:'Cinzel',serif; font-size:12pt; font-weight:600; color:var(--dark); letter-spacing:.04em; margin:14px 0 8px; }
  h4 { font-family:'Cinzel',serif; font-size:10pt; font-weight:600; color:var(--dim); letter-spacing:.08em; margin:12px 0 6px; text-transform:uppercase; }
  p { line-height:1.65; margin-bottom:8px; }
  .sub { color:var(--dim); font-style:italic; font-size:12pt; margin-bottom:12px; }
  .note { color:#8a6a44; font-size:10pt; font-style:italic; }
  .page-break { page-break-before:always; }

  /* ── TABLES ── */
  table { width:100%; border-collapse:collapse; font-size:11pt; margin-bottom:12px; }
  thead tr { background:rgba(184,115,51,.08); }
  th { font-family:'Cinzel',serif; font-size:8pt; letter-spacing:.1em; text-align:left; padding:7px 10px; color:var(--dim); border-bottom:1px solid rgba(184,115,51,.3); }
  td { padding:7px 10px; border-bottom:1px solid rgba(0,0,0,.06); vertical-align:top; }
  tfoot td { border-top:1px solid rgba(184,115,51,.3); font-weight:600; padding-top:9px; }
  .est-badge { display:inline-block; background:rgba(201,162,39,.15); border:1px solid rgba(201,162,39,.4); color:var(--gold); border-radius:3px; padding:1px 5px; font-size:8pt; font-family:'Cinzel',serif; margin-left:4px; }

  /* ── STEPS ── */
  .steps { margin:8px 0 16px; }
  .step { display:flex; gap:14px; margin-bottom:14px; page-break-inside:avoid; }
  .step-num { width:28px; height:28px; border-radius:50%; background:linear-gradient(135deg,var(--copper),var(--gold)); color:#fff; font-family:'Cinzel',serif; font-size:11pt; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .step-num.gold { background:linear-gradient(135deg,var(--gold),#e0bb55); }
  .step-body { flex:1; }
  .step-title { font-family:'Cinzel',serif; font-size:10pt; color:var(--copper); font-weight:600; margin-bottom:4px; }
  .tag { display:inline-block; background:rgba(184,115,51,.1); border:1px solid rgba(184,115,51,.25); border-radius:4px; padding:2px 8px; font-size:9pt; color:var(--dim); margin:4px 6px 4px 0; }
  .phase1-banner { border-left:3px solid var(--gold); padding-left:14px; }
  .phase1-banner h2 { color:var(--gold); }
  .safety { background:rgba(180,50,50,.06); border:1px solid rgba(180,50,50,.2); border-radius:6px; padding:8px 12px; font-size:10pt; color:#7a3030; margin-top:8px; }
  ul { padding-left:20px; margin:8px 0; }
  li { margin-bottom:4px; font-size:11pt; }

  /* ── ECO ── */
  .eco-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-bottom:16px; }
  .eco-card { border:1px solid rgba(184,115,51,.25); border-radius:7px; padding:12px 10px; text-align:center; }
  .eco-card.gold { background:rgba(201,162,39,.07); border-color:rgba(201,162,39,.4); }
  .eco-label { font-family:'Cinzel',serif; font-size:7pt; letter-spacing:.14em; color:var(--dim); text-transform:uppercase; margin-bottom:4px; }
  .eco-val { font-family:'Cinzel',serif; font-size:18pt; font-weight:700; color:var(--copper); }
  .eco-card.gold .eco-val { color:var(--gold); }
  .meta-row { display:flex; gap:16px; flex-wrap:wrap; margin-bottom:12px; font-size:11pt; }

  /* ── COCKTAILS ── */
  .cocktail-card { border:1px solid rgba(184,115,51,.25); border-radius:8px; padding:16px; margin-bottom:14px; page-break-inside:avoid; }
  .flavor { color:var(--dim); font-style:italic; margin-top:8px; font-size:11pt; }

  /* ── MARKET ── */
  .market-body { font-size:11.5pt; line-height:1.75; }
  .market-body p { margin-bottom:10px; }
  .market-body ul { padding-left:22px; margin:8px 0 12px; }
  .market-body li { margin-bottom:5px; }
  .mkt-h3 { font-family:'Cinzel',serif; font-size:12pt; font-weight:600; color:var(--copper); letter-spacing:.05em; margin:18px 0 8px; padding-bottom:4px; border-bottom:1px solid rgba(184,115,51,.2); }
  .mkt-h4 { font-family:'Cinzel',serif; font-size:10pt; font-weight:600; color:var(--dim); letter-spacing:.08em; margin:12px 0 6px; text-transform:uppercase; }
  .mkt-rule { border:none; border-top:1px solid rgba(184,115,51,.2); margin:14px 0; }

  /* ── SAFETY ── */
  .safety-clear { background:rgba(45,90,61,.05); border-left:3px solid #2d5a3d; padding-left:14px; }
  .safety-clear h2 { color:#2d5a3d; border-color:rgba(45,90,61,.3); }
  .safety-alert { border:1px solid rgba(180,50,50,.4); border-radius:8px; padding:16px; }
  .safety-alert h2 { color:#8b3030; border-color:rgba(180,50,50,.3); }
  .flag { border-left:3px solid #ccc; padding:10px 12px; margin:8px 0; border-radius:0 6px 6px 0; page-break-inside:avoid; }
  .flag-danger { border-color:#c05050; background:rgba(180,50,50,.04); }
  .flag-warning { border-color:#c9a227; background:rgba(201,162,39,.04); }
  .flag-caution { border-color:#b87333; background:rgba(184,115,51,.04); }
  .flag-head { margin-bottom:4px; font-size:11pt; }
  .flag-badge { display:inline-block; border:1px solid currentColor; border-radius:3px; padding:1px 6px; font-family:'Cinzel',serif; font-size:7pt; margin-left:8px; letter-spacing:.06em; }
  .flag-danger .flag-badge { color:#c05050; }
  .flag-warning .flag-badge { color:#c9a227; }
  .flag-caution .flag-badge { color:#b87333; }
  .flag-ref { color:#8a6a44; font-size:9pt; font-style:italic; margin-left:8px; }
  .flag-rec { color:#2d5a3d; font-style:italic; }
  .safety-disclaimer { font-size:9pt; color:#8a6a44; font-style:italic; margin-top:10px; border-top:1px solid rgba(0,0,0,.08); padding-top:8px; }

  /* ── FOOTER ── */
  .footer { position:fixed; bottom:0; left:0; right:0; text-align:center; font-family:'Cinzel',serif; font-size:7pt; letter-spacing:.18em; color:rgba(180,120,60,.5); padding:8px; border-top:1px solid rgba(184,115,51,.15); }

  /* ── PRINT BUTTON ── */
  .print-bar { position:fixed; top:0; left:0; right:0; background:#1a0e05; padding:12px 20px; display:flex; justify-content:space-between; align-items:center; z-index:999; border-bottom:1px solid rgba(184,115,51,.3); }
  .print-bar-title { font-family:'Cinzel',serif; color:#c9a227; font-size:12pt; letter-spacing:.1em; }
  .print-btn { background:linear-gradient(135deg,#b87333,#c9a227); border:none; border-radius:6px; color:#0a0704; padding:10px 24px; font-family:'Cinzel',serif; font-size:11pt; font-weight:700; cursor:pointer; letter-spacing:.08em; }
  .print-spacer { height:52px; }
</style>
</head><body>

<div class="print-bar no-print">
  <span class="print-bar-title">◈ SPIRITUS FORGE — Formula Sheet</span>
  <button class="print-btn" onclick="window.print()">⬇ Save as PDF</button>
</div>
<div class="print-spacer no-print"></div>

<!-- COVER PAGE -->
<div class="cover">
  <div class="cover-eyebrow">◈ Spiritus Forge — Craft Distillery Formulation Platform ◈</div>
  <div class="cover-title">${formula.productName}</div>
  ${formula.tagline?`<div class="cover-tagline">${formula.tagline}</div>`:""}
  <div class="cover-divider"></div>
  <div class="cover-meta">
    <div class="cover-meta-item"><div class="cover-meta-label">Target ABV</div><div class="cover-meta-val">${formula.targetABV}%</div></div>
    <div class="cover-meta-item"><div class="cover-meta-label">Batch Size</div><div class="cover-meta-val">${batch} ${batchUnit}</div></div>
    <div class="cover-meta-item"><div class="cover-meta-label">Bottles</div><div class="cover-meta-val">${ed?.bottles?.toLocaleString()||"—"}</div></div>
  </div>
  <div class="cover-ttb">
    ${formula.TTBCategory?`<span>${formula.TTBCategory}</span>`:""}
    ${formula.TTBSubcategory?`<span>${formula.TTBSubcategory}</span>`:""}
  </div>
  ${formula.flavorNotes?.length?`<div class="flavor-tags">${formula.flavorNotes.map(n=>`<span class="flavor-tag">${n}</span>`).join("")}</div>`:""}
  ${formula.description?`<div class="cover-notes">${formula.description}</div>`:""}
  ${formula.TTBNotes?`<div class="cover-notes" style="font-size:10pt;margin-top:8px;color:#7a5a3a">${formula.TTBNotes}</div>`:""}
  <div class="cover-date">Generated ${date} · Confidential Formula Document</div>
</div>

<!-- FORMULA -->
<div class="section page-break">
  <h2>Formula Specification</h2>
  <div class="meta-row">
    <span><b>Base Spirit:</b> ${formula.baseSpirit||"—"}</span>
    ${formula.baseSpiritProof?`<span><b>Base Proof:</b> ${formula.baseSpiritProof}-proof</span>`:""}
    <span><b>Target ABV:</b> ${formula.targetABV}%</span>
    ${formula.colorDescription?`<span><b>Appearance:</b> ${formula.colorDescription}</span>`:""}
  </div>
  ${ingSection("Core Ingredients", formula.coreIngredients, [{k:"ingredient",l:"Ingredient"},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Purpose"},{k:"source",l:"Source"}])}
  ${ingSection("Botanicals & Herbs", formula.botanicals, [{k:"ingredient",l:"Botanical"},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Flavor Note"},{k:"notes",l:"Notes"}])}
  ${ingSection("Colorings", formula.colorings, [{k:"ingredient",l:"Coloring"},{k:"amount",l:"Amount"},{k:"type",l:"Type"},{k:"notes",l:"Notes"}])}
  ${ingSection("Sweeteners", formula.sweeteners, [{k:"ingredient",l:"Sweetener"},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Purpose"}])}
  ${ingSection("Stabilizers", formula.stabilizers, [{k:"ingredient",l:"Stabilizer"},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Purpose"}])}
  ${ingSection("Commercial Flavoring Options", formula.commercialFlavorings, [{k:"name",l:"Product"},{k:"manufacturer",l:"Manufacturer"},{k:"usage",l:"Usage Rate"},{k:"notes",l:"Notes"}])}
  ${formula.additionalNotes?`<div style="margin-top:12px;padding:12px 14px;background:rgba(201,162,39,.05);border-left:3px solid rgba(201,162,39,.4);border-radius:0 6px 6px 0"><h4 style="margin-top:0">Formulation Notes</h4><p>${formula.additionalNotes}</p></div>`:""}
</div>

<!-- REGULATORY SAFETY CHECK -->
${safetyHTML}

<!-- BASE PRODUCTION (if needed) -->
${baseHTML}

<!-- PRODUCTION PROTOCOL -->
${productionHTML}

<!-- ECONOMICS -->
${ecoHTML}

<!-- COCKTAILS -->
${cocktailsHTML}

<!-- MARKET INTEL -->
${marketHTML}

<div class="footer">SPIRITUS FORGE ◈ AI FORMULATION SYSTEM ◈ CONFIDENTIAL — FOR LICENSED DISTILLERY USE ONLY</div>

<script>
  window.addEventListener('load', () => setTimeout(() => {
    if (window.location.search.includes('autoprint')) window.print();
  }, 800));
</script>
</body></html>`;

    const win = window.open("", "_blank");
    if (!win) { alert("Please allow pop-ups for this site to export the PDF."); return; }
    win.document.write(html);
    win.document.close();
  };

  const activeVertConfig = selectedVertical && selectedVertical !== "spirits" ? VERTICALS[selectedVertical] : null;
  const TABS=[{id:"workshop",label:"Formula Workshop"},{id:"production",label:"Production"},{id:"market",label:"Market Intel"},{id:"economics",label:"Economics"},{id:"cocktails",label:activeVertConfig?.tab5Label||"Cocktails"},{id:"label",label:"Label Generator"}];
  const ecoData=eco();
  const hasFormula=!!formula;

  const BtnPrimary=({onClick,children,style:sx={},disabled=false})=>(<button onClick={onClick} disabled={disabled} style={{background:disabled?"#333":`linear-gradient(135deg,${C.copper} 0%,${C.copperL} 60%,${C.gold} 100%)`,border:"none",borderRadius:7,color:disabled?C.dim:"#0a0704",padding:"12px 26px",fontSize:13,fontWeight:700,fontFamily:F.h,cursor:disabled?"default":"pointer",letterSpacing:"0.09em",...sx}}>{children}</button>);
  const BtnOutline=({onClick,children,active=false,style:sx={}})=>(<button onClick={onClick} style={{background:active?`${C.copper}22`:"transparent",border:`1px solid ${active?C.copper:C.border}`,borderRadius:7,color:active?C.copperL:C.dim,padding:"9px 18px",fontSize:12,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.08em",...sx}}>{children}</button>);


  const processLabelImage = async (file) => {
    if (file.size > 5 * 1024 * 1024) { setLabelErr("Image too large — please use a file under 5MB."); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const rawB64 = e.target.result.split(",")[1];
      const mtype = file.type || "image/jpeg";
      // Compress to max 800px / 0.72 quality — keeps under Vercel 4.5MB body limit
      const img = new Image();
      img.onload = async () => {
        const MAX = 800;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const b64 = canvas.toDataURL("image/jpeg", 0.72).split(",")[1];
        setBrandImageB64(b64);
        setBrandImageType("image/jpeg");
        setBrandAnalyzing(true);
        setBrandAnalysis(null);
      try {
        const body = {
          model: MODEL,
          max_tokens: 1000,
          system: "Brand identity analyst for beverage labels. Return only valid JSON, no markdown.",
          messages: [{
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: mtype, data: b64 } },
              { type: "text", text: `You are a label design analyst. Study this beverage label carefully and extract its complete visual DNA. Return ONLY valid JSON:
{
  "colors": {
    "background": "#hex only",
    "backgroundMid": "#hex only",
    "primary": "#hex only",
    "secondary": "#hex only",
    "accent": "#hex only",
    "border": "#hex only",
    "subtext": "#hex only",
    "dim": "#hex only"
  },
  "style": "2-4 words: e.g. 'bold rustic dark' or 'clean minimal white' or 'ornate vintage gold'",
  "typography": "serif|sans-serif|script|mixed",
  "mood": "2-3 words",
  "toneNotes": "one sentence on copywriting voice",
  "shape": "portrait-rectangle|landscape-rectangle|tall-narrow|square|oval|hexagon|shield|custom",
  "layout": {
    "zones": "describe the label zones top-to-bottom e.g. 'top crest, large center name, bottom band' or 'full-bleed illustration, center text overlay, bottom strip'",
    "namePosition": "top|center|bottom|lower-center",
    "nameSize": "dominant|large|medium — how large is the brand name relative to label",
    "textAlignment": "center|left|mixed",
    "hasTopCrest": true or false,
    "hasBottomBand": true or false,
    "hasCenterIllustration": true or false,
    "hasFrameBorder": true or false,
    "hasBackgroundTexture": true or false
  },
  "imagery": {
    "type": "none|animal|landscape|crest|geometric|botanical|abstract|portrait|building|celestial",
    "description": "one sentence describing the main visual element e.g. 'a bald eagle with wings spread over mountain peaks' or 'concentric hexagonal geometric pattern' or 'rolling wheat fields at sunset'",
    "position": "top|center|bottom|background|top-center",
    "svgApproach": "describe in 1-2 sentences how to approximate this with basic SVG shapes — e.g. 'draw a triangle for mountain peak with two smaller triangles behind, add a circle for sun above' or 'draw overlapping hexagons rotated 30deg' or 'use curved paths for rolling hills with a circle sun'"
  },
  "designElements": "one sentence on key decorative motifs"
}` }
            ]
          }]
        };
        const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error?.message || "Vision API error");
        const text = (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("");
        const parsed = parseJSON(text);
        if (parsed?.colors) {
          // Strip any description text after the hex value (e.g. "#1a2b3c — description" → "#1a2b3c")
          const cleanColors = {};
          Object.entries(parsed.colors).forEach(([k, v]) => {
            const match = String(v).match(/#[0-9a-fA-F]{3,6}/);
            cleanColors[k] = match ? match[0] : v;
          });
          setBrandAnalysis({ ...parsed, colors: cleanColors });
        } else setBrandAnalysis(null);
      } catch(e) {
        console.error("Brand analysis failed:", e.message);
        setBrandAnalysis(null);
      } finally {
        setBrandAnalyzing(false);
      }
      }; // end img.onload
      img.src = "data:" + mtype + ";base64," + rawB64;
    };
    reader.readAsDataURL(file);
  };

  const resetAll=()=>{ setFormula(null); setSafetyCheck(null); setProduction(null); setProductionErr(""); setDebugLog([]); setMarket(null); setCocktails(null); setLabelData(null); setLabelBrief(null); setLabelSvg(null); setLabelErr(""); setBrandImageB64(null); setBrandImageType(null); setBrandAnalysis(null); setTtbCategory(""); setBaseSpirit(""); setActiveMethod(0); setFlavorInput(""); setIngredientInput(""); setCostRows([]); setCostEstimateNote(""); setCostEstimating(false); };

  return (
    <div style={{minHeight:"100vh",background:C.bg,color:C.text,fontFamily:F.b}}>

      {/* ── PASSWORD GATE ── */}
      {!authed && (
        <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:`linear-gradient(160deg,#0f0a05 0%,#1a0e05 50%,#0f0a05 100%)`,padding:24}}>
          <div style={{width:"100%",maxWidth:420}}>
            <div style={{textAlign:"center",marginBottom:36}}>
              <div style={{fontSize:10,color:C.copper,fontFamily:F.h,letterSpacing:"0.5em",marginBottom:14,textTransform:"uppercase"}}>◈ Craft Distillery Formulation Platform ◈</div>
              <h1 style={{fontFamily:F.h,fontSize:"clamp(28px,6vw,44px)",letterSpacing:"0.22em",margin:"0 0 10px",background:`linear-gradient(135deg,${C.copper} 0%,${C.goldL} 50%,${C.copperL} 100%)`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>SPIRITUS FORGE</h1>
              <p style={{color:C.dim,fontFamily:F.b,fontSize:15,fontStyle:"italic",margin:0}}>AI-Powered Spirit Formulation Laboratory</p>
              <div style={{width:120,height:1,background:`linear-gradient(90deg,transparent,${C.border},transparent)`,margin:"20px auto 0"}}/>
            </div>
            <div style={{background:C.card,border:`1px solid ${C.borderH}`,borderRadius:12,padding:"28px 28px 24px"}}>
              <div style={{fontFamily:F.h,color:C.copperL,fontSize:13,letterSpacing:"0.12em",marginBottom:6,textAlign:"center"}}>BETA ACCESS</div>
              <p style={{color:C.dim,fontSize:14,fontStyle:"italic",textAlign:"center",marginBottom:22,lineHeight:1.55}}>This platform is in private beta. Enter your access password to continue.</p>
              <label style={S.label}>Access Password</label>
              <input
                type="password"
                value={pwInput}
                onChange={e=>{setPwInput(e.target.value);setPwError("");}}
                onKeyDown={e=>{
                  if(e.key==="Enter"){
                    if(pwInput===BETA_PASSWORD){try{sessionStorage.setItem("sf_authed","1");}catch{}setAuthed(true);}
                    else{setPwError("Incorrect password. Please try again.");setPwInput("");}
                  }
                }}
                placeholder="Enter beta access password"
                style={{...S.inp({marginBottom:pwError?8:16}),fontSize:16,letterSpacing:"0.12em"}}
                autoFocus
              />
              {pwError && <div style={{color:C.redL,fontSize:13,marginBottom:14,textAlign:"center"}}>{pwError}</div>}
              <button
                onClick={()=>{
                  if(pwInput===BETA_PASSWORD){try{sessionStorage.setItem("sf_authed","1");}catch{}setAuthed(true);}
                  else{setPwError("Incorrect password. Please try again.");setPwInput("");}
                }}
                style={{width:"100%",background:`linear-gradient(135deg,${C.copper},${C.gold})`,border:"none",borderRadius:7,color:"#0a0704",padding:"13px",fontSize:13,fontWeight:700,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.1em"}}>
                ENTER
              </button>
            </div>
            <p style={{color:C.dim2,fontSize:12,textAlign:"center",marginTop:16,fontStyle:"italic"}}>
              Don't have a password? Contact us to request beta access.
            </p>
          </div>
        </div>
      )}

      {/* ── MAIN APP (shown after auth) ── */}

      {/* ── VERTICAL SELECTOR (shown after auth, before main app) ── */}
      {authed && !selectedVertical && (
        <div style={{minHeight:"100vh",background:"#080604",color:"#ecddc8",fontFamily:F.b}}>
          <div style={{height:3,background:"linear-gradient(90deg,transparent 0%,#b87333 25%,#c9a227 50%,#d4943a 75%,transparent 100%)"}}/>
          <header style={{textAlign:"center",padding:"32px 24px 24px",borderBottom:"1px solid rgba(184,115,51,0.2)"}}>
            <div style={{fontSize:10,color:"#b87333",fontFamily:F.h,letterSpacing:"0.5em",marginBottom:10,textTransform:"uppercase"}}>◈ Regulated Product Formulation Platform ◈</div>
            <h1 style={{fontFamily:F.h,fontSize:"clamp(26px,5vw,46px)",letterSpacing:"0.22em",margin:"0 0 8px",background:"linear-gradient(135deg,#b87333 0%,#e0bb55 50%,#d4943a 100%)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>FORGE</h1>
            <p style={{color:"#8a7a62",fontFamily:F.b,fontSize:14,fontStyle:"italic",margin:0}}>AI-Powered Formulation for Craft Beverage & Consumer Product Brands</p>
          </header>
          <VerticalSelector onSelect={v => setSelectedVertical(v)} C={C} F={F}/>
          <footer style={{textAlign:"center",padding:"20px",borderTop:"1px solid rgba(184,115,51,0.15)",color:"#6a5a44",fontSize:11,fontFamily:F.h,letterSpacing:"0.18em"}}>FORGE PLATFORM ◈ AI FORMULATION ◈ spiritusforge.io</footer>
          <style>{`*{box-sizing:border-box}button:hover{border-color:rgba(184,115,51,0.55)!important;background:rgba(184,115,51,0.08)!important}`}</style>
        </div>
      )}
      {authed && selectedVertical && (<>
      <div style={{height:3,background:`linear-gradient(90deg,transparent 0%,${C.copper} 25%,${C.gold} 50%,${C.copperL} 75%,transparent 100%)`}}/>
      <header style={{textAlign:"center",padding:"32px 24px 22px",position:"relative",borderBottom:`1px solid ${C.border}`,background:`linear-gradient(180deg,#0f0c08 0%,${C.bg} 100%)`}}>
        <div style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)"}}>
          <button onClick={()=>{setSelectedVertical(null); resetAll();}} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,color:C.dim,padding:"6px 12px",fontSize:11,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.1em"}}>← Change</button>
        </div>
        <div style={{fontSize:10,color:C.copper,fontFamily:F.h,letterSpacing:"0.5em",marginBottom:10,textTransform:"uppercase"}}>◈ Craft Distillery Formulation Platform ◈</div>
        <h1 style={{fontFamily:F.h,fontSize:"clamp(26px,5vw,50px)",letterSpacing:"0.22em",margin:"0 0 8px",background:`linear-gradient(135deg,${C.copper} 0%,${C.goldL} 50%,${C.copperL} 100%)`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>SPIRITUS FORGE</h1>
        <p style={{color:C.dim,fontFamily:F.b,fontSize:15,fontStyle:"italic",margin:0}}>AI-Powered Spirit Formulation Laboratory for Craft Distillers</p>
        {hasFormula && (
          <div style={{marginTop:14}}>
            <button onClick={exportPDF} style={{background:`linear-gradient(135deg,${C.copper},${C.gold})`,border:"none",borderRadius:7,color:"#0a0704",padding:"10px 24px",fontSize:12,fontWeight:700,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.1em"}}>
              ⬇ Export Formula PDF
            </button>
          </div>
        )}
      </header>

      <nav style={{display:"flex",borderBottom:`1px solid ${C.border}`,background:C.card,overflowX:"auto",scrollbarWidth:"none",msOverflowStyle:"none"}}>
        {TABS.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={{border:"none",borderBottom:tab===t.id?`2px solid ${C.copper}`:"2px solid transparent",borderRadius:0,padding:"12px 14px",background:"transparent",color:tab===t.id?C.copperL:C.dim,fontSize:11,fontFamily:F.h,fontWeight:tab===t.id?700:400,letterSpacing:"0.07em",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>{t.label}{t.id!=="workshop"&&!hasFormula?" 🔒":""}</button>)}
      </nav>

      <main style={{maxWidth:1120,margin:"0 auto",padding:"30px 18px"}}>

        {/* ══ WORKSHOP ══ */}
        {tab==="workshop"&&(
          <div>
            {!hasFormula&&!loading&&(
              <div style={{maxWidth:680,margin:"0 auto"}}>
                <SectionTitle title="Formula Design Studio" sub="Describe what you want to create — by flavor vision, by known ingredients, or both"/>
                <div style={{display:"flex",gap:0,marginBottom:22,border:`1px solid ${C.border}`,borderRadius:9,overflow:"hidden"}}>
                  {[["profile","Describe Flavor Profile"],["ingredients","Known Ingredients"]].map(([v,l])=><button key={v} onClick={()=>setMode(v)} style={{flex:1,padding:"13px",border:"none",background:mode===v?`${C.copper}1e`:"transparent",color:mode===v?C.copperL:C.dim,fontFamily:F.h,fontSize:12,letterSpacing:"0.09em",cursor:"pointer",fontWeight:mode===v?700:400,borderRight:v==="profile"?`1px solid ${C.border}`:"none"}}>{l}</button>)}
                </div>
                {mode==="profile"?(
                  <div style={{marginBottom:18}}><label style={S.label}>{activeVertConfig?.inputLabel||"Desired Flavor Profile"}</label><textarea value={flavorInput} onChange={e=>setFlavorInput(e.target.value)} placeholder={activeVertConfig?.inputPlaceholder||'"Bright and citrusy with fresh ginger, warm cardamom, a hint of lavender, and a clean dry finish."'}  style={{...S.inp(),height:100,resize:"vertical"}}/></div>
                ):(
                  <>
                    <div style={{marginBottom:18}}><label style={S.label}>Known or Desired Ingredients</label><textarea value={ingredientInput} onChange={e=>setIngredientInput(e.target.value)} placeholder={"juniper berries, coriander, angelica root, lemon peel, rose petals…"} style={{...S.inp(),height:90,resize:"vertical"}}/></div>
                    <div style={{marginBottom:18}}><label style={S.label}>Additional Flavor Direction (optional)</label><input value={flavorInput} onChange={e=>setFlavorInput(e.target.value)} placeholder={"e.g. Should be floral, elegant, slightly sweet…"} style={S.inp()}/></div>
                  </>
                )}
                {!activeVertConfig && <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                  <div>
                    <label style={S.label}>TTB Category</label>
                    <select value={ttbCategory} onChange={e=>setTtbCategory(e.target.value)} style={{...S.inp(),cursor:"pointer"}}>{buildOptgroups(TTB_CATEGORIES)}</select>
                    {ttbCategory&&<div style={{fontSize:11,color:C.greenL,fontFamily:F.h,marginTop:4,letterSpacing:"0.06em"}}>✓ Formula will comply with {ttbCategory} standards</div>}
                  </div>
                  <div>
                    <label style={S.label}>Base Spirit</label>
                    <select value={baseSpirit} onChange={e=>setBaseSpirit(e.target.value)} style={{...S.inp(),cursor:"pointer"}}>{buildOptgroups(BASE_SPIRITS)}</select>
                    {baseSpirit&&<div style={{fontSize:11,color:C.greenL,fontFamily:F.h,marginTop:4,letterSpacing:"0.06em"}}>✓ Built on {baseSpirit.split("(")[0].trim()}</div>}
                  </div>
                </div>}
                <div style={{marginBottom:26}}>
                  <label style={S.label}>Batch Size</label>
                  <div style={{display:"flex",gap:10}}>
                    <input value={batch} onChange={e=>setBatch(e.target.value)} type="text" inputMode="decimal" style={{...S.inp({flex:1})}} placeholder="53"/>
                    <select value={batchUnit} onChange={e=>setBatchUnit(e.target.value)} style={{...S.inp({width:110,cursor:"pointer"})}}><option value="gallons">Gallons</option><option value="liters">Liters</option></select>
                  </div>
                </div>
                {err&&<div style={{background:`${C.red}1a`,border:`1px solid ${C.red}55`,borderRadius:7,padding:"12px 16px",color:"#e89090",fontFamily:F.b,marginBottom:16}}>{err}</div>}
                <BtnPrimary onClick={generate} style={{width:"100%",padding:"15px",fontSize:14}}>⚗ GENERATE FORMULA</BtnPrimary>
                <p style={{color:C.dim2,fontSize:13,textAlign:"center",marginTop:10,fontStyle:"italic"}}>Generates formula · production protocols · market analysis · cocktail recipes</p>
              </div>
            )}
            {loading&&<LoadingSpinner msg={status}/>}
            {hasFormula&&!loading&&(
              <div>
                <div style={S.card({marginBottom:18,background:`linear-gradient(135deg,#1c1710,#12100b)`,borderColor:C.borderH})}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16,marginBottom:16}}>
                    <div style={{flex:1,minWidth:200}}>
                      <div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.24em",marginBottom:6}}>GENERATED FORMULA</div>
                      <h1 style={{fontFamily:F.h,fontSize:"clamp(22px,4vw,34px)",letterSpacing:"0.1em",margin:"0 0 6px",background:`linear-gradient(135deg,${C.copperL},${C.goldL})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>{formula.productName}</h1>
                      {formula.tagline&&<p style={{color:C.dim,fontStyle:"italic",margin:"0 0 10px",fontSize:15}}>{formula.tagline}</p>}
                      <p style={{color:C.text,lineHeight:1.65,margin:0,maxWidth:520,fontSize:15}}>{formula.description}</p>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:12,minWidth:155}}>
                      <div style={{...S.card({padding:"14px 16px"}),textAlign:"center"}}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.16em"}}>TARGET ABV</div><div style={{color:C.gold,fontSize:30,fontFamily:F.h,fontWeight:700}}>{formula.targetABV ? formula.targetABV+"%" : (activeVertConfig?.id==="nonalc" ? "0%" : "—")}</div>{formula.baseSpiritProof&&<div style={{color:C.dim,fontSize:11}}>{formula.baseSpiritProof}-proof base</div>}</div>
                      <div style={{...S.card({padding:"12px 16px"}),textAlign:"center"}}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.14em"}}>BASE SPIRIT</div><div style={{color:C.text,fontSize:13,fontFamily:F.h}}>{formula.baseSpirit}</div></div>
                    </div>
                  </div>
                  <Divider/>
                  <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
                    <span style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.14em"}}>TTB:</span>
                    {formula.TTBCategory&&<Tag label={formula.TTBCategory} col={C.gold}/>}
                    {formula.TTBSubcategory&&<Tag label={formula.TTBSubcategory} col={C.copper}/>}
                    {formula.TTBNotes&&<span style={{color:C.dim,fontSize:13,fontStyle:"italic"}}>{formula.TTBNotes}</span>}
                  </div>
                  {formula.flavorNotes?.length>0&&<div><span style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.14em",marginRight:10}}>TASTING NOTES:</span>{formula.flavorNotes.map((n,i)=><Tag key={i} label={n} col={C.copperL}/>)}</div>}
                  {formula.colorDescription&&<p style={{color:C.dim,fontSize:13,fontStyle:"italic",margin:"10px 0 0"}}>Color: {formula.colorDescription}</p>}
                </div>

                {/* ── SAFETY CHECK PANEL ── */}
                {safetyCheck && (()=>{
                  const sc = safetyCheck;
                  const isDanger = sc.status === "danger";
                  const isWarning = sc.status === "warning";
                  const isClear = sc.status === "clear";
                  const isUnknown = sc.status === "unknown";
                  const borderCol = isDanger ? `${C.red}88` : isWarning ? `${C.gold}88` : isClear ? `${C.green}66` : C.border;
                  const bgCol = isDanger ? `${C.red}12` : isWarning ? `${C.gold}0c` : isClear ? `${C.green}0f` : `${C.border}22`;
                  const icon = isDanger ? "🚫" : isWarning ? "⚠" : isClear ? "✅" : "❓";
                  const headCol = isDanger ? C.redL : isWarning ? C.gold : isClear ? C.greenL : C.dim;
                  const headline = isDanger ? "⚠ Possible Prohibited Ingredient Detected — Review Required" : isWarning ? "CAUTION — Regulatory Flags Require Review" : isClear ? "Safety Check Passed — All Ingredients Cleared" : "Safety Check Incomplete";
                  return (
                    <div style={{background:bgCol, border:`2px solid ${borderCol}`, borderRadius:10, padding:"18px 20px", marginBottom:18}}>
                      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                        <span style={{fontSize:22}}>{icon}</span>
                        <div>
                          <div style={{fontFamily:F.h,color:headCol,fontSize:14,letterSpacing:"0.08em",fontWeight:700}}>{headline}</div>
                          <div style={{color:C.dim,fontSize:13,marginTop:3,fontStyle:"italic"}}>{sc.overallSummary}</div>
                        </div>
                      </div>
                      {sc.flags?.length > 0 && (
                        <div style={{marginTop:12}}>
                          {sc.flags.map((flag,fi)=>{
                            const fc = flag.severity==="danger" ? C.redL : flag.severity==="warning" ? C.gold : C.copperL;
                            return (
                              <div key={fi} style={{background:`rgba(0,0,0,0.2)`, border:`1px solid ${fc}44`, borderLeft:`3px solid ${fc}`, borderRadius:"0 7px 7px 0", padding:"12px 14px", marginBottom:10}}>
                                <div style={{display:"flex",gap:10,alignItems:"baseline",flexWrap:"wrap",marginBottom:6}}>
                                  <span style={{fontFamily:F.h,color:fc,fontSize:13,letterSpacing:"0.05em"}}>{flag.ingredient}</span>
                                  <span style={{...S.tag(fc),marginBottom:0}}>{flag.severity.toUpperCase()}</span>
                                  {flag.regulation && <span style={{color:C.dim2,fontSize:11,fontStyle:"italic"}}>Ref: {flag.regulation}</span>}
                                </div>
                                <p style={{color:C.text,fontSize:14,margin:"0 0 6px",lineHeight:1.5}}>{flag.issue}</p>
                                {flag.recommendation && (
                                  <div style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:8}}>
                                    <span style={{color:C.greenL,fontSize:12,flexShrink:0}}>→</span>
                                    <span style={{color:C.greenL,fontSize:13,fontStyle:"italic"}}>{flag.recommendation}</span>
                                  </div>
                                )}
                                {flag.severity==="danger" && (
                                  <button onClick={(e)=>{
                                    e.stopPropagation();
                                    const sub = flag.recommendation||"";
                                    const note = sub ? ` Substitute: use ${sub} instead of ${flag.ingredient}.` : ` Exclude ${flag.ingredient}.`;
                                    setFlavorInput(prev => (prev||"") + note);
                                    setSafetyCheck(null);
                                    setFormula(null);
                                    setProduction(null);
                                    setMarket(null);
                                    setCocktails(null);
                                    setTab("workshop");
                                    setTimeout(()=>window.scrollTo({top:0,behavior:"smooth"}),100);
                                  }} style={{background:`${C.green}22`,border:`1px solid ${C.green}55`,borderRadius:6,color:C.greenL,padding:"5px 12px",fontSize:11,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.08em",marginTop:4}}>
                                    ⟳ Regenerate With Safe Substitute
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {isClear && sc.clearIngredients?.length > 0 && (
                        <div style={{marginTop:8,display:"flex",gap:6,flexWrap:"wrap"}}>
                          {sc.clearIngredients.map((ing,i)=><Tag key={i} label={`✓ ${ing}`} col={C.green}/>)}
                        </div>
                      )}
                      <p style={{color:C.dim2,fontSize:11,margin:"10px 0 0",fontStyle:"italic"}}>
                        ⚠ This is an AI-assisted regulatory screening tool, not a substitute for professional TTB compliance review or FDA regulatory counsel. Always verify with a licensed compliance specialist before commercial production.
                      </p>
                      {isUnknown && <button onClick={retrySafetyCheck} style={{marginTop:10,background:"transparent",border:`1px solid ${C.border}`,borderRadius:6,color:C.dim,padding:"6px 14px",fontSize:11,fontFamily:F.h,cursor:"pointer",letterSpacing:"0.1em"}}>⟳ Retry Safety Check</button>}
                    </div>
                  );
                })()}

                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14,marginBottom:16}}>
                  {formula.coreIngredients?.length>0&&<div style={S.card2()}><IngTable title="Core Ingredients" items={formula.coreIngredients} cols={[{k:"ingredient",l:"Ingredient",hi:true},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Purpose"}]}/></div>}
                  {formula.botanicals?.length>0&&<div style={S.card2()}><IngTable title="Botanicals & Herbs" items={formula.botanicals} cols={[{k:"ingredient",l:"Botanical",hi:true},{k:"amount",l:"Amount"},{k:"unit",l:"Unit"},{k:"purpose",l:"Note Added"}]}/></div>}
                  {formula.colorings?.length>0&&<div style={S.card2()}><IngTable title="Colorings" items={formula.colorings} cols={[{k:"ingredient",l:"Coloring",hi:true},{k:"amount",l:"Amount"},{k:"type",l:"Type"},{k:"notes",l:"Notes"}]}/></div>}
                  {formula.sweeteners?.length>0&&<div style={S.card2()}><IngTable title="Sweeteners" items={formula.sweeteners} cols={[{k:"ingredient",l:"Sweetener",hi:true},{k:"amount",l:"Amount"},{k:"purpose",l:"Purpose"}]}/></div>}
                  {formula.stabilizers?.length>0&&<div style={S.card2()}><IngTable title="Stabilizers" items={formula.stabilizers} cols={[{k:"ingredient",l:"Stabilizer",hi:true},{k:"amount",l:"Amount"},{k:"purpose",l:"Purpose"}]}/></div>}
                  {formula.commercialFlavorings?.length>0&&<div style={S.card2()}><IngTable title="Commercial Flavoring Options" items={formula.commercialFlavorings} cols={[{k:"name",l:"Product",hi:true},{k:"manufacturer",l:"Manufacturer"},{k:"usage",l:"Usage Rate"}]}/></div>}
                </div>

                {formula.pricingSuggestion&&(
                  <div style={S.card({marginBottom:14})}>
                    <h2 style={S.h2}>Suggested Pricing</h2>
                    <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:12}}>
                      <MetricBox label={activeVertConfig ? "Producer MSRP" : "Distillery MSRP"} value={fmt$(formula.pricingSuggestion.distilleryMSRP)} col={C.gold} note="Sell at tasting room"/>
                      <MetricBox label="Distributor Price" value={fmt$(formula.pricingSuggestion.distributorPrice)} col={C.copper} note="Wholesale to distrib."/>
                      <MetricBox label="Retail MSRP" value={fmt$(formula.pricingSuggestion.retailMSRP)} col={C.copperL} note="On-shelf price"/>
                    </div>
                    {formula.pricingSuggestion.rationale&&<p style={{color:C.dim,fontSize:13,fontStyle:"italic",margin:0}}>{formula.pricingSuggestion.rationale}</p>}
                  </div>
                )}
                {formula.additionalNotes&&<div style={S.card({borderColor:`${C.gold}33`,marginBottom:14})}><h3 style={{...S.h3,color:C.gold}}>Formulation Notes</h3><p style={{color:C.text,lineHeight:1.7,margin:0,fontSize:15}}>{formula.additionalNotes}</p></div>}
                <div style={{display:"flex",gap:12,marginTop:20,flexWrap:"wrap"}}>
                  <BtnOutline onClick={resetAll}>← New Formula</BtnOutline>
                  <BtnPrimary onClick={()=>setTab("production")}>View Production Steps →</BtnPrimary>
                  <BtnPrimary onClick={exportPDF} style={{background:`linear-gradient(135deg,${C.green},${C.greenL})`}}>⬇ Export PDF</BtnPrimary>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ PRODUCTION ══ */}
        {tab==="production"&&(
          <div>
            {!production&&!productionErr&&!loading&&<EmptyState msg="Generate a formula first to view production protocols."/>}
            {loading&&!production&&<LoadingSpinner msg={status||"Building production protocols…"}/>}
            {productionErr&&!production&&(
              <div style={S.card({borderColor:productionErr.includes("Usage resets")?`${C.gold}66`:`${C.red}55`,margin:"40px auto",maxWidth:580,textAlign:"center"})}>
                <div style={{fontSize:32,marginBottom:12}}>{productionErr.includes("Usage resets")?"⏳":"⚠"}</div>
                <h3 style={{...S.h3,color:productionErr.includes("Usage resets")?C.gold:C.redL,fontSize:16,marginBottom:8}}>{productionErr.includes("Usage resets")?"API Usage Limit Reached":"Production Protocol Error"}</h3>
                <p style={{color:C.dim,fontSize:14,lineHeight:1.65,margin:"0 0 20px"}}>{productionErr}</p>
                <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginBottom:16}}>
                  {!productionErr.includes("Usage resets")&&<BtnPrimary onClick={retryProduction}>⟳ Retry Production</BtnPrimary>}
                  <BtnOutline onClick={()=>{setProductionErr("");setTab("workshop");}}>← Back to Workshop</BtnOutline>
                </div>

              </div>
            )}
            {production&&(
              <div>
                <SectionTitle title="Production Protocols" sub={`${production.baseProduction?.phases?.length?"Phase 1: Base Spirit + Phase 2: Finishing · ":""}${formula?.productName||"your spirit"} · ${batch} ${batchUnit} batch`}/>
                {production.baseProduction?.phases?.length>0&&(
                  <div style={{marginBottom:32}}>
                    <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:18,flexWrap:"wrap"}}>
                      <div style={{background:`${C.gold}18`,border:`1px solid ${C.gold}44`,borderRadius:9,padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                        <span style={{fontFamily:F.h,color:C.gold,fontSize:13,letterSpacing:"0.14em"}}>PHASE 1</span>
                        <span style={{color:C.dim2}}>|</span><span style={{fontFamily:F.h,color:C.goldL,fontSize:15}}>Produce the Base Spirit</span>
                        <span style={{color:C.dim2}}>|</span><span style={{color:C.dim,fontSize:13}}>{production.baseProduction.baseName}</span>
                      </div>
                      {production.baseProduction.totalTimeRequired&&<span style={{color:C.dim,fontSize:12}}>⏱ {production.baseProduction.totalTimeRequired}</span>}
                      {production.baseProduction.difficulty&&<span style={S.tag(C.gold)}>{production.baseProduction.difficulty}</span>}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 280px",gap:18}}>
                      <div>
                        {production.baseProduction.overview&&<div style={S.card({marginBottom:14,borderColor:`${C.gold}33`})}><p style={{color:C.dim,lineHeight:1.65,margin:0,fontSize:15}}>{production.baseProduction.overview}</p></div>}
                        {(production.baseProduction.phases||[]).map((phase,pi)=><PhaseBlock key={pi} phase={phase} index={pi}/>)}
                        {production.baseProduction.qualityCheckpoints?.length>0&&<div style={S.card({borderColor:`${C.gold}44`})}><h3 style={{...S.h3,color:C.gold}}>Quality Checkpoints</h3>{production.baseProduction.qualityCheckpoints.map((q,i)=><div key={i} style={{display:"flex",gap:10,marginBottom:8}}><span style={{color:C.gold}}>◆</span><span style={{color:C.text,fontSize:14}}>{q}</span></div>)}</div>}
                      </div>
                      <div>
                        <div style={S.card2({marginBottom:14})}><h3 style={S.h3}>Base Spirit Overview</h3>{[["Base Name",production.baseProduction.baseName],["Total Time",production.baseProduction.totalTimeRequired],["Difficulty",production.baseProduction.difficulty]].filter(([,v])=>v).map(([k,v])=><div key={k} style={{marginBottom:12}}><div style={{color:C.dim2,fontSize:10,fontFamily:F.h,letterSpacing:"0.13em"}}>{k.toUpperCase()}</div><div style={{color:C.text,fontSize:13}}>{v}</div></div>)}</div>
                        <div style={S.card2({marginBottom:14})}><h3 style={S.h3}>Equipment Needed</h3>{(production.baseProduction.equipment||[]).map((eq,i)=><div key={i} style={{color:C.text,fontSize:13,marginBottom:6,display:"flex",gap:8}}><span style={{color:C.gold}}>▪</span>{eq}</div>)}</div>
                        {production.baseProduction.safetyNotes?.length>0&&<div style={S.card2({borderColor:`${C.red}33`})}><h3 style={{...S.h3,color:C.redL}}>Safety Notes</h3>{production.baseProduction.safetyNotes.map((n,i)=><div key={i} style={{color:C.dim,fontSize:13,marginBottom:6}}>⚠ {n}</div>)}</div>}
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:16,margin:"28px 0 8px"}}>
                      <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${C.border})`}}/>
                      <div style={{background:`${C.copper}22`,border:`1px solid ${C.copper}44`,borderRadius:20,padding:"6px 18px",fontFamily:F.h,color:C.copper,fontSize:11,letterSpacing:"0.14em"}}>PHASE 2 — FORMULA FINISHING</div>
                      <div style={{flex:1,height:1,background:`linear-gradient(270deg,transparent,${C.border})`}}/>
                    </div>
                  </div>
                )}
                {production.methods?.length>0&&(
                  <div>
                    <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
                      {production.methods.map((m,i)=><BtnOutline key={i} onClick={()=>setActiveMethod(i)} active={activeMethod===i}>{m.name}<span style={{marginLeft:8,fontSize:10,color:activeMethod===i?C.copperL:C.dim2}}>({m.difficulty})</span></BtnOutline>)}
                    </div>
                    {production.methods[activeMethod]&&<MethodPanel method={production.methods[activeMethod]}/>}
                  </div>
                )}

              </div>
            )}
          </div>
        )}

        {/* ══ MARKET INTEL ══ */}
        {tab==="market"&&(
          <div>
            {!market&&!loading&&<EmptyState msg="Generate a formula first to see market analysis."/>}
            {loading&&!market&&<LoadingSpinner msg={status||"Scanning the market…"}/>}
            {market&&(
              <div>
                <SectionTitle title="Market Intelligence" sub={`Competitive landscape for ${formula?.TTBCategory||"your spirit category"}`}/>
                <div style={S.card()}>
                  {market.length<100&&(market.includes("unavailable")||market.includes("limit"))?
                    <div style={{textAlign:"center",padding:20}}><p style={{color:C.redL,fontFamily:F.h,fontSize:14,marginBottom:12}}>⚠ Market research could not be completed</p><p style={{color:C.dim,fontSize:14}}>{market}</p><BtnPrimary onClick={async()=>{
                      setMarket(null); setLoading(true); setStatus("🔍 Retrying market research…");
                      try{
                        const mp=`You are a US spirits industry analyst. List 4-5 specific real competing products for: ${formula.TTBCategory||"craft spirit"}, flavor: ${flavorInput||ingredientInput}, ${formula.targetABV}%ABV. For each give: product name, brand, state, price per 750ml, distribution, and flavor profile. End with a positioning recommendation.`;
                        const md=await callClaude(mp,"Senior US spirits market analyst. Use only real brand names from your knowledge.",false,3000);
                        setMarket(getText(md)||"No results returned. Try regenerating.");
                      }catch(e){setMarket("Market research unavailable. "+e.message.replace("RATE_LIMIT:",""));}
                      finally{setLoading(false);setStatus("");}
                    }} style={{marginTop:8}}>⟳ Retry Market Research</BtnPrimary></div>
                  :
                    <div style={{lineHeight:1.75,color:C.text,fontFamily:F.b,fontSize:14}}>
                      {(()=>{
                        const lines = market.split("\n");
                        const elements = [];
                        let inSection = null;
                        let sectionLines = [];
                        const flushSection = () => {
                          if(inSection && sectionLines.length){
                            const isCompetitor = /competitor|direct|product/i.test(inSection);
                            const isPositioning = /position|recommend|differentiat/i.test(inSection);
                            const isContext = /context|trend|market/i.test(inSection);
                            const bg = isCompetitor ? `${C.copper}0d` : isPositioning ? `${C.green}0d` : `${C.border}22`;
                            const border = isCompetitor ? C.copper : isPositioning ? C.greenL : C.dim2;
                            elements.push(
                              <div key={inSection} style={{background:bg,border:`1px solid ${border}44`,borderLeft:`3px solid ${border}`,borderRadius:"0 8px 8px 0",padding:"14px 16px",marginBottom:14}}>
                                <div style={{fontFamily:F.h,color:border,fontSize:11,letterSpacing:"0.14em",marginBottom:8,textTransform:"uppercase"}}>{inSection}</div>
                                {sectionLines.map((sl,si)=>{
                                  const t=sl.trim();
                                  if(!t) return null;
                                  const fmt=t.replace(/\*\*([^*]+)\*\*/g,"<b style='color:"+C.copperL+"'>$1</b>").replace(/\*([^*]+)\*/g,"<i>$1</i>");
                                  const isBullet = t.startsWith("-") || t.startsWith("•") || /^\d+\./.test(t);
                                  return <p key={si} style={{margin:"0 0 6px",paddingLeft:isBullet?8:0,fontSize:13,color:C.text}} dangerouslySetInnerHTML={{__html:fmt}}/>;
                                })}
                              </div>
                            );
                            sectionLines = [];
                          }
                        };
                        lines.forEach((line,i)=>{
                          const t=line.trim();
                          if(t.startsWith("# ")||t.startsWith("## ")||t.startsWith("### ")){
                            flushSection();
                            inSection=t.replace(/^#+\s*/,"");
                          } else if(t.startsWith("---")){
                            flushSection(); inSection=null;
                          } else if(inSection){
                            sectionLines.push(line);
                          } else if(t){
                            const fmt=t.replace(/\*\*([^*]+)\*\*/g,"<b style='color:"+C.copperL+"'>$1</b>").replace(/\*([^*]+)\*/g,"<i>$1</i>");
                            elements.push(<p key={i} style={{margin:"0 0 10px",fontSize:14,color:C.text}} dangerouslySetInnerHTML={{__html:fmt}}/>);
                          }
                        });
                        flushSection();
                        return elements;
                      })()}
                    </div>
                  }
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ ECONOMICS ══ */}
        {tab==="economics"&&(
          <div>
            <SectionTitle title="Economics & Pricing" sub="Track ingredient costs, calculate COGS, and optimize margins"/>
            {!formula?<EmptyState msg="Generate a formula first to track costs and calculate margins."/>:(
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 320px",gap:18,alignItems:"start"}}>
                <div>
                  <div style={S.card({marginBottom:16})}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:16}}>
                      <div>
                        <h2 style={{...S.h2,margin:0}}>Ingredient Costs — Per Batch</h2>
                        <p style={{color:C.dim,fontSize:13,margin:"4px 0 0",fontStyle:"italic"}}>Enter your actual supplier costs, or let AI estimate them</p>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                        {costRows.some(r=>r.estimated) && (
                          <BtnOutline onClick={()=>setCostRows(r=>r.map(row=>({...row,estimated:false,unitNote:"",confidence:""})))} style={{fontSize:11,color:C.dim}}>
                            ✎ Clear Estimates
                          </BtnOutline>
                        )}
                        <BtnPrimary onClick={estimateCosts} disabled={costEstimating} style={{fontSize:12,padding:"9px 18px"}}>
                          {costEstimating ? "⏳ Estimating…" : "✨ Auto-Estimate Costs"}
                        </BtnPrimary>
                      </div>
                    </div>

                    {costEstimateNote && (
                      <div style={{background:`${C.gold}0f`,border:`1px solid ${C.gold}33`,borderRadius:7,padding:"10px 14px",marginBottom:14,display:"flex",gap:10,alignItems:"flex-start"}}>
                        <span style={{color:C.gold,fontSize:14,flexShrink:0}}>ℹ</span>
                        <p style={{color:C.dim,fontSize:12,margin:0,lineHeight:1.5,fontStyle:"italic"}}>{costEstimateNote}</p>
                      </div>
                    )}

                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:14,fontFamily:F.b}}>
                      <thead>
                        <tr>
                          {["Ingredient","Qty / Batch","Unit Price Note","Batch Cost ($)",""].map(h=>(
                            <th key={h} style={{textAlign:"left",color:C.dim,padding:"5px 8px 10px 0",fontSize:10,fontFamily:F.h,letterSpacing:"0.12em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {costRows.map((row,i)=>(
                          <tr key={i} style={{borderBottom:`1px solid ${C.border}18`}}>
                            <td style={{padding:"8px 8px 8px 0",color:C.copperL,fontSize:14,verticalAlign:"middle"}}>
                              {row.name}
                              {row.estimated && (
                                <span style={{marginLeft:6,fontSize:10,background:`${C.gold}22`,border:`1px solid ${C.gold}44`,color:C.gold,borderRadius:4,padding:"1px 6px",fontFamily:F.h,letterSpacing:"0.06em",verticalAlign:"middle"}}>
                                  {row.confidence==="high"?"EST ●":row.confidence==="low"?"EST ○":"EST"}
                                </span>
                              )}
                            </td>
                            <td style={{padding:"8px 8px 8px 0",color:C.dim,fontSize:12,verticalAlign:"middle"}}>{row.desc}</td>
                            <td style={{padding:"8px 8px 8px 0",color:C.dim2,fontSize:11,fontStyle:"italic",verticalAlign:"middle",maxWidth:140}}>{row.unitNote||"—"}</td>
                            <td style={{padding:"6px 8px 6px 0",verticalAlign:"middle"}}>
                              <input
                                value={row.cost}
                                onChange={e=>{const u=[...costRows];u[i]={...u[i],cost:e.target.value,estimated:false,unitNote:u[i].unitNote||""};setCostRows(u);}}
                                placeholder="0.00"
                                style={{...S.inp({width:90,padding:"6px 10px"}),fontFamily:F.m,borderColor:row.estimated?`${C.gold}66`:`${C.border}`}}
                              />
                            </td>
                            <td style={{verticalAlign:"middle"}}>
                              <button onClick={()=>setCostRows(costRows.filter((_,ri)=>ri!==i))} style={{background:"none",border:"none",color:C.dim2,cursor:"pointer",fontSize:16,padding:"4px 6px",lineHeight:1}} title="Remove row">×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3} style={{paddingTop:14,color:C.dim,fontFamily:F.h,fontSize:11,letterSpacing:"0.12em"}}>
                            TOTAL INGREDIENT COST
                            {costRows.some(r=>r.estimated)&&<span style={{marginLeft:8,color:C.gold,fontSize:10}}>★ includes estimates</span>}
                          </td>
                          <td style={{paddingTop:14,color:C.gold,fontFamily:F.m,fontSize:18,fontWeight:700}} colSpan={2}>
                            ${costRows.reduce((a,r)=>a+(parseFloat(r.cost)||0),0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>

                    <div style={{marginTop:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <BtnOutline onClick={()=>setCostRows([...costRows,{name:"",desc:"",cost:"",estimated:false,unitNote:""}])} style={{fontSize:11}}>+ Add Row</BtnOutline>
                      {costRows.some(r=>r.estimated)&&(
                        <span style={{color:C.dim2,fontSize:11,fontStyle:"italic"}}>● High confidence &nbsp; ○ Low confidence &nbsp; EST = AI estimate — click cost to override</span>
                      )}
                    </div>
                  </div>
                  <div style={S.card()}>
                    <h2 style={S.h2}>Production Cost Inputs</h2>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                      {[["Bottle Cost (each)",bottleCost,setBottleCost,"$"],["Label & Closure (each)",labelCost,setLabelCost,"$"],["Labor Hours per Batch",laborH,setLaborH,"hrs"],["Labor Rate",laborR,setLaborR,"$/hr"],["Overhead Markup",overhead,setOverhead,"%"],["Bottle Size",bottleML,setBottleML,"mL"]].map(([label,val,setter,suffix])=><div key={label}><label style={S.label}>{label}</label><div style={{display:"flex",gap:6,alignItems:"center"}}><input value={val} onChange={e=>setter(e.target.value)} style={{...S.inp({flex:1}),fontFamily:F.m}}/><span style={{color:C.dim2,fontSize:12,whiteSpace:"nowrap"}}>{suffix}</span></div></div>)}
                    </div>
                  </div>
                </div>
                <div>
                  {ecoData&&(<>
                    <div style={S.card({marginBottom:14,borderColor:`${C.gold}44`})}>
                      <h2 style={S.h2}>Batch Economics</h2>
                      {[[(activeVertConfig?"Units from Batch":"Bottles from Batch"),ecoData.bottles.toLocaleString(),C.text],["Total Ingredient Cost",fmt$(ecoData.ingCost),C.text],["Labor Cost",fmt$(ecoData.labCost),C.text],[(activeVertConfig?"Packaging per Unit":"Packaging per Bottle"),fmt$(ecoData.pkgPerB),C.text],[(activeVertConfig?"Raw COGS / Unit":"Raw COGS / Bottle"),fmt$(ecoData.cogsRaw),C.copperL],["COGS with Overhead",fmt$(ecoData.cogs),C.gold]].map(([k,v,col])=><div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}22`}}><span style={{color:C.dim,fontSize:13}}>{k}</span><span style={{color:col,fontFamily:F.m,fontSize:13}}>{v}</span></div>)}
                    </div>
                    <div style={S.card({marginBottom:14})}>
                      <h2 style={S.h2}>Pricing Tiers</h2>
                      {[[activeVertConfig ? "Producer MSRP" : "Distillery MSRP",fmt$(ecoData.dMSRP),C.gold],["Distributor Price",fmt$(ecoData.distP),C.copper],["Retail MSRP",fmt$(ecoData.retP),C.copperL]].map(([k,v,col])=><div key={k} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px solid ${C.border}22`}}><span style={{color:C.dim,fontSize:13}}>{k}</span><span style={{color:col,fontFamily:F.m,fontSize:17,fontWeight:700}}>{v}</span></div>)}
                      <div style={{marginTop:14,padding:"16px",background:ecoData.margin>45?`${C.green}18`:ecoData.margin>25?`${C.gold}14`:`${C.red}14`,borderRadius:8,textAlign:"center"}}>
                        <div style={{color:C.dim,fontSize:10,fontFamily:F.h,letterSpacing:"0.16em",marginBottom:4}}>{activeVertConfig ? "GROSS MARGIN AT PRODUCER" : "GROSS MARGIN AT DISTILLERY"}</div>
                        <div style={{fontSize:38,fontFamily:F.h,fontWeight:700,color:ecoData.margin>45?C.greenL:ecoData.margin>25?C.gold:C.redL}}>{fmtN(ecoData.margin)}%</div>
                        <div style={{color:C.dim,fontSize:12,marginTop:2}}>{ecoData.margin>55?"Excellent":ecoData.margin>40?"Healthy":ecoData.margin>25?"Review pricing":"⚠ Low — adjust costs"}</div>
                      </div>
                    </div>
                    <div style={S.card2()}>
                      <h3 style={S.h3}>Batch Revenue Potential</h3>
                      {[[(activeVertConfig?"All units at producer":"All bottles at distillery"),ecoData.bottles*ecoData.dMSRP],[(activeVertConfig?"All units at distributor":"All bottles at distributor"),ecoData.bottles*ecoData.distP]].map(([k,v])=><div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0"}}><span style={{color:C.dim,fontSize:13}}>{k}</span><span style={{color:C.text,fontFamily:F.m}}>{fmtK(v)}</span></div>)}
                    </div>
                  </>)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ COCKTAILS ══ */}
        {tab==="cocktails"&&(
          <div>
            {!cocktails&&!loading&&<EmptyState msg="Generate a formula first to see cocktail recipes."/>}
            {loading&&!cocktails&&<LoadingSpinner msg={status||"Crafting cocktail recipes…"}/>}
            {cocktails&&(
              <div>
                <SectionTitle title={activeVertConfig ? (activeVertConfig.tab5Label||"Pairings") : "Cocktail Laboratory"} sub={`Signature serves featuring ${formula?.productName||"your spirit"}`}/>
                {cocktails.error&&<div style={{...S.card({borderColor:`${C.red}44`,marginBottom:16,textAlign:"center"})}}>
                  <p style={{color:C.redL,marginBottom:12}}>⚠ Cocktail recipes could not be generated: {cocktails.error}</p>
                  <BtnPrimary onClick={async()=>{
                    setCocktails(null); setLoading(true); setStatus("🍸 Retrying cocktail recipes…");
                    const styles=[
                      {style:"easy approachable tasting room serve",occasion:"distillery tasting room"},
                      {style:"sophisticated on-trend bartender cocktail",occasion:"craft cocktail bar"},
                    ];
                    const results=[];
                    for(let ci=0;ci<styles.length;ci++){
                      setStatus(`🍸 Retrying cocktail ${ci+1} of 2…`);
                      const cs=styles[ci];
                      const cp=`Create one ${cs.style} featuring: ${formula.productName} (${(formula.flavorNotes||[]).slice(0,3).join(", ")||flavorInput}, ${formula.targetABV}%ABV). Return ONLY one valid JSON object (not an array), no markdown:
{"name":"name","description":"One sentence.","style":"${cs.style.split(" ").slice(0,3).join(" ")}","ingredients":[{"item":"item","amount":"1.5 oz","notes":""},{"item":"item","amount":"0.5 oz","notes":""},{"item":"item","amount":"0.75 oz","notes":""}],"method":"Shake","ice":"Large cube","glassware":"Rocks glass","garnish":"garnish","flavorProfile":"Brief taste.","difficulty":"Easy","occasion":"${cs.occasion}"}`;
                      try{
                        const cd=await callClaude(cp,"Expert cocktail consultant. Return only one valid JSON object, no markdown.",false,1000);
                        let parsed=parseJSON(getText(cd));
                        if(parsed?.cocktails?.length)parsed=parsed.cocktails[0];
                        if(parsed?.name)results.push(parsed);
                      }catch(e){ if(handleRateLimit(e,()=>{}))break; }
                    }
                    setCocktails(results.length?{cocktails:results}:{cocktails:[],error:"Still could not generate. Please regenerate the formula."});
                    setLoading(false); setStatus("");
                  }}>⟳ Retry Cocktails</BtnPrimary>
                </div>}
                {cocktails.cocktails?.length>0&&(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:18}}>
                    {cocktails.cocktails.map((c,i)=><CocktailCard key={i} cocktail={c} index={i} cardLabel={activeVertConfig?"PAIRING":"COCKTAIL"}/>)}
                  </div>
                )}
                {cocktails.cocktails?.length===0&&!cocktails.error&&<EmptyState msg="No cocktails in this batch — try the Retry button."/>}
              </div>
            )}
          </div>
        )}


        {/* ══ LABEL GENERATOR ══ */}
        {tab==="label"&&(
          <div>
            <SectionTitle title="Label Generator" sub="AI-generated label copy & compliance spec ready for your designer"/>
            {!hasFormula&&<EmptyState msg="Generate a formula first to create label copy."/>}
            {hasFormula&&!labelData&&!labelLoading&&(
              <div style={{maxWidth:640,margin:"0 auto"}}>
                <div style={S.card({marginBottom:20})}>
                  <h3 style={S.h3}>Distillery Info</h3>
                  <p style={{color:C.dim,fontSize:13,marginBottom:16}}>Used for the bottler statement on your label.</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
                    <div style={{gridColumn:"1/-1"}}><label style={S.label}>Distillery / Bottler Name</label><input value={labelDistillery} onChange={e=>setLabelDistillery(e.target.value)} placeholder="e.g. Desert Sun Distilling Co." style={S.inp()}/></div>
                    <div><label style={S.label}>City</label><input value={labelCity} onChange={e=>setLabelCity(e.target.value)} placeholder="e.g. Austin" style={S.inp()}/></div>
                    <div><label style={S.label}>State</label><input value={labelState} onChange={e=>setLabelState(e.target.value)} placeholder="e.g. TX" style={S.inp()}/></div>
                  </div>
                  <div style={{maxWidth:200}}><label style={S.label}>Bottle Size (ml)</label>
                    <select value={labelVolume} onChange={e=>setLabelVolume(e.target.value)} style={S.inp()}>
                      {["50","100","200","375","750","1000","1750"].map(v=><option key={v} value={v}>{v} ml{v==="750"?" (standard)":""}</option>)}
                    </select>
                  </div>
                </div>

                {/* Brand Upload */}
                <div style={S.card({marginBottom:20,borderColor:brandAnalysis?`${C.greenL}44`:C.border})}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                    <div>
                      <h3 style={{...S.h3,marginBottom:4}}>Existing Label <span style={{color:C.dim,fontWeight:400,fontSize:12}}>(optional)</span></h3>
                      <p style={{color:C.dim,fontSize:12,margin:0}}>Upload a current label — AI will match your brand colors, typography style, and copy tone.</p>
                    </div>
                    {brandAnalysis&&<span style={{...S.tag(C.greenL),flexShrink:0,marginTop:2}}>✓ Brand analyzed</span>}
                  </div>
                  {!brandImageB64?(
                    <label style={{display:"block",cursor:"pointer"}}>
                      <div style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"20px",textAlign:"center",transition:"border-color 0.2s"}}
                        onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.copper;}}
                        onDragLeave={e=>{e.currentTarget.style.borderColor=C.border;}}
                        onDrop={e=>{
                          e.preventDefault();e.currentTarget.style.borderColor=C.border;
                          const file=e.dataTransfer.files[0];
                          if(file)processLabelImage(file);
                        }}>
                        <div style={{fontSize:28,marginBottom:8}}>🏷</div>
                        <div style={{color:C.dim,fontSize:13,marginBottom:4}}>Drop a label image here, or click to browse</div>
                        <div style={{color:C.dim2,fontSize:11}}>JPG · PNG · WEBP · up to 5MB</div>
                      </div>
                      <input type="file" accept="image/jpeg,image/png,image/webp" style={{display:"none"}}
                        onChange={e=>{const f=e.target.files[0]; if(f)processLabelImage(f);}}/>
                    </label>
                  ):(
                    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                      <img src={`data:${brandImageType};base64,${brandImageB64}`} alt="Uploaded label" style={{width:90,height:120,objectFit:"cover",borderRadius:6,border:`1px solid ${C.border}`,flexShrink:0}}/>
                      <div style={{flex:1}}>
                        {brandAnalyzing&&<div style={{color:C.gold,fontSize:13,marginBottom:8}}>⚗ Analyzing brand identity…</div>}
                        {brandAnalysis&&(
                          <div>
                            <div style={{color:C.greenL,fontSize:13,marginBottom:10}}>✓ Brand identity extracted</div>
                            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                              {Object.entries(brandAnalysis.colors||{}).slice(0,5).map(([k,v])=>(
                                <div key={k} title={k} style={{width:20,height:20,borderRadius:4,background:v,border:`1px solid ${C.border}`,flexShrink:0}}/>
                              ))}
                              <span style={{color:C.dim,fontSize:11,alignSelf:"center",marginLeft:4}}>Brand palette</span>
                            </div>
                            {brandAnalysis.style&&<div style={{color:C.dim,fontSize:12,fontStyle:"italic",marginBottom:6}}>"{brandAnalysis.style}"</div>}
                            {brandAnalysis.toneNotes&&<div style={{color:C.dim,fontSize:12}}>{brandAnalysis.toneNotes}</div>}
                          </div>
                        )}
                        <button onClick={()=>{setBrandImageB64(null);setBrandImageType(null);setBrandAnalysis(null);}} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:5,color:C.dim,padding:"5px 12px",fontSize:11,fontFamily:F.h,cursor:"pointer",marginTop:8,letterSpacing:"0.08em"}}>× Remove</button>
                      </div>
                    </div>
                  )}
                </div>

                {labelErr&&<div style={{...S.card({borderColor:`${C.red}44`,marginBottom:16,textAlign:"center"})}}><p style={{color:C.redL,margin:0}}>{labelErr}</p></div>}
                {brandAnalyzing&&<div style={{...S.card({marginBottom:16,borderColor:`${C.gold}44`,padding:"12px 16px",textAlign:"center"})}}><p style={{color:C.gold,margin:0,fontSize:13}}>⚗ Analyzing brand identity — please wait before generating…</p></div>}
                <BtnPrimary onClick={async()=>{
                  if(brandAnalyzing){setLabelErr("Please wait for brand analysis to complete first.");return;}
                  setLabelLoading(true); setLabelErr("");
                  const vertical = selectedVertical||"spirits";
                  const isAlc = vertical==="spirits"||vertical==="brewing"||vertical==="wine";
                  const isTTB = isAlc;
                  const dist = labelDistillery||"[Distillery Name]";
                  const city = labelCity||"[City]";
                  const st = labelState||"[State]";
                  const vol = labelVolume||"750";
                  const proof = formula.targetABV ? Math.round(formula.targetABV*2) : null;
                  const allIngNames = [
                    ...(formula.coreIngredients||[]).map(i=>i.ingredient),
                    ...(formula.botanicals||[]).map(i=>i.ingredient),
                    ...(formula.sweeteners||[]).map(i=>i.ingredient),
                    ...(formula.colorings||[]).map(i=>i.ingredient),
                    ...(formula.stabilizers||[]).map(i=>i.ingredient),
                    ...(formula.commercialFlavorings||[]).map(i=>i.name),
                  ].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);

                  const brandContext = brandAnalysis ? `

CRITICAL — EXISTING BRAND IDENTITY TO MATCH:
The distillery uploaded their existing label. You MUST mirror their brand voice and style.
- Visual style: ${brandAnalysis.style||"not specified"}
- Typography: ${brandAnalysis.typography||"not specified"}
- Mood: ${brandAnalysis.mood||"not specified"}
- Copy tone: ${brandAnalysis.toneNotes||"not specified"}
- Design elements: ${brandAnalysis.designElements||"not specified"}
- Colors: ${JSON.stringify(brandAnalysis.colors||{})}
Write the tagline, tasting notes, serving suggestion, and designNotes so they sound like they belong on the same label family as the uploaded label. Match the voice precisely.` : "";

                  const ingredientList = isTTB ? "null — TTB spirits do not require ingredient list" : `INGREDIENTS: ${allIngNames.slice(0,8).join(", ")}`;
                  const prompt = `You are a TTB/FDA label compliance expert. Generate a label spec for this product.${brandContext}

PRODUCT DATA:
- Name: ${formula.productName}
- Tagline: ${formula.tagline||"none"}
- Category: ${formula.TTBCategory||""} ${formula.TTBSubcategory||""}
- ABV: ${formula.targetABV}%${proof?` (${proof} proof)`:""}
- Bottle size: ${vol}ml
- Vertical: ${vertical}
- Distillery: ${dist}, ${city}, ${st}
- Key ingredients: ${allIngNames.slice(0,10).join(", ")}
- Flavor notes: ${(formula.flavorNotes||[]).join(", ")}
- Description: ${formula.description||"none"}
- TTB notes: ${formula.TTBNotes||"none"}

Generate a complete label spec. Return ONLY valid JSON, no markdown:
{
  "frontLabel": {
    "brandName": "exact brand/product name",
    "tagline": "tagline or null",
    "classTypeStatement": "exact TTB class and type statement (e.g. 'Distilled Spirits Specialty' or 'Straight Bourbon Whiskey')",
    "statementOfComposition": "only if required for DSS or non-standard — e.g. 'Agave-Based Distilled Spirits Specialty with Natural Flavors' or null",
    "alcoholContent": "${formula.targetABV}% Alc/Vol${proof?` (${proof} Proof)`:""}",
    "netContents": "${vol} mL",
    "governmentWarning": "GOVERNMENT WARNING: (1) According to the Surgeon General, women should not drink alcoholic beverages during pregnancy because of the risk of birth defects. (2) Consumption of alcoholic beverages impairs your ability to drive a car or operate machinery, and may cause health problems.",
    "bottlerStatement": "Distilled and Bottled by ${dist}, ${city}, ${st}",
    "designNotes": "2-3 sentences of creative direction for the front label designer — colors, mood, imagery, style that fits the product character"
  },
  "backLabel": {
    "tastingNotes": "2-3 sentence tasting note for back label (consumer-friendly)",
    "servingSuggestion": "one serving suggestion sentence",
    "ingredientStatement": "${ingredientList}",
    "allergenStatement": "CONTAINS: [any allergens present] or null if none",
    "recyclingStatement": "Please recycle.",
    "websiteOrSocial": "spiritusforge.io or null",
    "barcode": "UPC barcode required — obtain from GS1 US"
  },
  "complianceChecklist": [
    {"item": "checklist item", "required": true, "status": "required|recommended|optional", "notes": "brief note"}
  ],
  "colaReadiness": {
    "score": 0-100,
    "summary": "one sentence readiness assessment",
    "blockers": ["any items that would prevent COLA approval"],
    "warnings": ["items to verify before submission"]
  }
}`;

                  try {
                    const resp = await callClaude(prompt, "TTB and FDA label compliance expert. Return only valid compact JSON. No markdown, no backticks, no commentary. Start your response with { and end with }.", false, 4000);
                    const raw = getText(resp);
                    // Robust parse — try multiple extraction strategies
                    let parsed = parseJSON(raw);
                    // If top-level parse failed, try extracting just the JSON object
                    if (!parsed?.frontLabel) {
                      const start = raw.indexOf("{");
                      const end = raw.lastIndexOf("}");
                      if (start !== -1 && end > start) {
                        try { parsed = JSON.parse(raw.slice(start, end + 1)); } catch {}
                      }
                    }
                    // If still no frontLabel, try to build a minimal valid response from whatever we got
                    if (!parsed?.frontLabel && parsed) {
                      // maybe it returned fields at top level
                      if (parsed.brandName || parsed.classTypeStatement) {
                        parsed = { frontLabel: parsed, backLabel: {}, complianceChecklist: [], colaReadiness: null };
                      }
                    }
                    if (!parsed?.frontLabel) throw new Error("Could not parse label data — please try generating again.");
                    // Ensure required fields have fallbacks
                    parsed.frontLabel.governmentWarning = parsed.frontLabel.governmentWarning ||
                      "GOVERNMENT WARNING: (1) According to the Surgeon General, women should not drink alcoholic beverages during pregnancy because of the risk of birth defects. (2) Consumption of alcoholic beverages impairs your ability to drive a car or operate machinery, and may cause health problems.";
                    parsed.backLabel = parsed.backLabel || {};
                    parsed.complianceChecklist = parsed.complianceChecklist || [];
                    setLabelData(parsed);
                  } catch(e) {
                    setLabelErr(e.message.replace("RATE_LIMIT:","Usage limit reached — try again later. "));
                  } finally { setLabelLoading(false); }
                }} style={{width:"100%",padding:"14px"}}>✦ Generate Label Copy</BtnPrimary>
              </div>
            )}
            {labelLoading&&<LoadingSpinner msg="Generating label specification…"/>}
            {labelData&&(
              <div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
                  <div/>
                  <div style={{display:"flex",gap:10}}>
                    <BtnOutline onClick={()=>{setLabelData(null);setLabelBrief(null);setLabelErr("");}} style={{fontSize:11}}>⟳ Regenerate</BtnOutline>
                    <BtnPrimary onClick={()=>{
                      const fl=labelData.frontLabel; const bl=labelData.backLabel; const cl=labelData.complianceChecklist||[]; const cr=labelData.colaReadiness;
                      const scoreCol = cr?.score>=80?"#4a7c59":cr?.score>=60?"#c9a227":"#8b3030";
                      const win=window.open("","_blank");
                      if(!win){alert("Allow pop-ups to export.");return;}
                      win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Label Spec — ${formula.productName}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Crimson Text',Georgia,serif;font-size:12pt;color:#1a0e05;background:#fff}
@page{margin:18mm 16mm;size:letter}@media print{.no-print{display:none}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
.print-bar{position:fixed;top:0;left:0;right:0;background:#1a0e05;padding:10px 20px;display:flex;justify-content:space-between;align-items:center;z-index:999;border-bottom:1px solid rgba(184,115,51,.3)}.print-bar span{font-family:'Cinzel',serif;color:#c9a227;font-size:11pt;letter-spacing:.1em}.print-btn{background:linear-gradient(135deg,#b87333,#c9a227);border:none;border-radius:6px;color:#0a0704;padding:9px 22px;font-family:'Cinzel',serif;font-size:10pt;font-weight:700;cursor:pointer;letter-spacing:.08em}
.spacer{height:50px}
.cover{page-break-after:always;min-height:95vh;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;background:linear-gradient(160deg,#1a0e05,#2e1a08,#1a0e05);padding:60px 40px;color:#ecddc8}
.cover-eye{font-family:'Cinzel',serif;font-size:8pt;letter-spacing:.5em;color:#b87333;margin-bottom:20px;text-transform:uppercase}
.cover-title{font-family:'Cinzel',serif;font-size:40pt;font-weight:700;letter-spacing:.12em;background:linear-gradient(135deg,#b87333,#c9a227);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin-bottom:10px;line-height:1.1}
.cover-tag{font-size:15pt;font-style:italic;color:#c9a88a;margin-bottom:30px}
.cover-div{width:160px;height:1px;background:linear-gradient(90deg,transparent,#b87333,transparent);margin:0 auto 30px}
.cover-sub{font-family:'Cinzel',serif;font-size:10pt;letter-spacing:.25em;color:#8a6a3a;text-transform:uppercase}
.cover-score{margin-top:28px;border:1px solid ${scoreCol}55;border-radius:8px;padding:14px 28px;display:inline-block}
.cover-score-label{font-family:'Cinzel',serif;font-size:8pt;letter-spacing:.2em;color:#8a6a3a;text-transform:uppercase;margin-bottom:6px}
.cover-score-val{font-family:'Cinzel',serif;font-size:28pt;font-weight:700;color:${scoreCol}}
.cover-score-sub{font-size:10pt;color:#8a6a3a;margin-top:4px}
h2{font-family:'Cinzel',serif;font-size:14pt;font-weight:600;color:#b87333;letter-spacing:.06em;margin:0 0 10px;padding-bottom:6px;border-bottom:1px solid rgba(184,115,51,.3)}
h3{font-family:'Cinzel',serif;font-size:11pt;font-weight:600;color:#2e1a08;letter-spacing:.04em;margin:16px 0 8px}
h4{font-family:'Cinzel',serif;font-size:9pt;font-weight:600;color:#6b4c2a;letter-spacing:.1em;margin:12px 0 5px;text-transform:uppercase}
.section{margin-bottom:24px;page-break-inside:avoid}
.page-break{page-break-before:always}
.field{margin-bottom:14px;padding:12px 14px;background:rgba(201,162,39,.04);border:1px solid rgba(184,115,51,.15);border-radius:6px}
.field-label{font-family:'Cinzel',serif;font-size:8pt;letter-spacing:.16em;color:#8a6a44;text-transform:uppercase;margin-bottom:5px}
.field-value{font-size:12pt;line-height:1.6;color:#1a0e05}
.field-value.warning-text{font-size:10pt;color:#5a3a1a;font-style:italic;background:rgba(201,162,39,.06);border-radius:4px;padding:6px 8px;margin-top:4px}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check-table{width:100%;border-collapse:collapse;font-size:11pt;margin-top:8px}
.check-table th{font-family:'Cinzel',serif;font-size:8pt;letter-spacing:.1em;text-align:left;padding:6px 10px;color:#6b4c2a;border-bottom:1px solid rgba(184,115,51,.3)}
.check-table td{padding:8px 10px;border-bottom:1px solid rgba(0,0,0,.06);vertical-align:top}
.badge{display:inline-block;border-radius:3px;padding:2px 8px;font-family:'Cinzel',serif;font-size:8pt;letter-spacing:.06em;font-weight:600}
.badge-req{background:rgba(184,115,51,.12);color:#8a4a1a;border:1px solid rgba(184,115,51,.3)}
.badge-rec{background:rgba(201,162,39,.12);color:#7a6010;border:1px solid rgba(201,162,39,.3)}
.badge-opt{background:rgba(100,120,100,.1);color:#4a6a4a;border:1px solid rgba(100,120,100,.25)}
.blocker{background:rgba(180,50,50,.05);border-left:3px solid #c05050;border-radius:0 6px 6px 0;padding:8px 12px;margin:6px 0;font-size:11pt}
.warn-item{background:rgba(201,162,39,.05);border-left:3px solid #c9a227;border-radius:0 6px 6px 0;padding:8px 12px;margin:6px 0;font-size:11pt}
.footer{position:fixed;bottom:0;left:0;right:0;text-align:center;font-family:'Cinzel',serif;font-size:7pt;letter-spacing:.18em;color:rgba(180,120,60,.5);padding:8px;border-top:1px solid rgba(184,115,51,.15)}
p{line-height:1.65;margin-bottom:8px}
</style></head><body>
<div class="print-bar no-print"><span>◈ SPIRITUS FORGE — Label Specification</span><button class="print-btn" onclick="window.print()">⬇ Save as PDF</button></div>
<div class="spacer no-print"></div>
<div class="cover">
  <div class="cover-eye">◈ Spiritus Forge — Label Generator ◈</div>
  <div class="cover-title">${formula.productName}</div>
  ${fl.tagline?`<div class="cover-tag">${fl.tagline}</div>`:""}
  <div class="cover-div"></div>
  <div class="cover-sub">Label Specification &amp; COLA Readiness Report</div>
  ${cr?`<div class="cover-score"><div class="cover-score-label">COLA Readiness Score</div><div class="cover-score-val">${cr.score}/100</div><div class="cover-score-sub">${cr.summary}</div></div>`:""}
</div>
<div class="section page-break">
  <h2>Front Label</h2>
  <div class="field"><div class="field-label">Brand / Product Name</div><div class="field-value" style="font-size:18pt;font-weight:600">${fl.brandName}</div></div>
  ${fl.tagline?`<div class="field"><div class="field-label">Tagline</div><div class="field-value" style="font-style:italic">${fl.tagline}</div></div>`:""}
  <div class="two-col">
    <div class="field"><div class="field-label">Class &amp; Type Statement</div><div class="field-value">${fl.classTypeStatement}</div></div>
    <div class="field"><div class="field-label">Alcohol Content</div><div class="field-value">${fl.alcoholContent}</div></div>
    <div class="field"><div class="field-label">Net Contents</div><div class="field-value">${fl.netContents}</div></div>
    <div class="field"><div class="field-label">Bottler Statement</div><div class="field-value">${fl.bottlerStatement}</div></div>
  </div>
  ${fl.statementOfComposition?`<div class="field"><div class="field-label">Statement of Composition</div><div class="field-value">${fl.statementOfComposition}</div></div>`:""}
  <div class="field"><div class="field-label">Government Warning (mandatory)</div><div class="field-value warning-text">${fl.governmentWarning}</div></div>
  ${fl.designNotes?`<div class="field" style="border-color:rgba(201,162,39,.3);background:rgba(201,162,39,.04)"><div class="field-label" style="color:#8a6010">Design Direction</div><div class="field-value" style="font-style:italic;color:#5a4010">${fl.designNotes}</div></div>`:""}
</div>
<div class="section">
  <h2>Back Label</h2>
  <div class="field"><div class="field-label">Tasting Notes</div><div class="field-value">${bl.tastingNotes}</div></div>
  <div class="field"><div class="field-label">Serving Suggestion</div><div class="field-value">${bl.servingSuggestion}</div></div>
  ${bl.ingredientStatement?`<div class="field"><div class="field-label">Ingredient Statement</div><div class="field-value">${bl.ingredientStatement}</div></div>`:""}
  ${bl.allergenStatement?`<div class="field"><div class="field-label">Allergen Statement</div><div class="field-value">${bl.allergenStatement}</div></div>`:""}
  <div class="two-col">
    <div class="field"><div class="field-label">Recycling</div><div class="field-value">${bl.recyclingStatement||"Please recycle."}</div></div>
    ${bl.barcode?`<div class="field"><div class="field-label">Barcode</div><div class="field-value">${bl.barcode}</div></div>`:""}
  </div>
</div>
<div class="section page-break">
  <h2>Compliance Checklist</h2>
  <table class="check-table"><thead><tr><th>Item</th><th>Status</th><th>Notes</th></tr></thead><tbody>
  ${cl.map(c=>`<tr><td>${c.item}</td><td><span class="badge ${c.status==="required"?"badge-req":c.status==="recommended"?"badge-rec":"badge-opt"}">${c.status.toUpperCase()}</span></td><td style="color:#6b4c2a;font-size:10pt">${c.notes||""}</td></tr>`).join("")}
  </tbody></table>
</div>
${cr?`<div class="section">
  <h2>COLA Readiness Assessment</h2>
  <div style="display:flex;align-items:center;gap:20px;margin-bottom:16px">
    <div style="width:70px;height:70px;border-radius:50%;border:3px solid ${scoreCol};display:flex;align-items:center;justify-content:center;flex-shrink:0"><span style="font-family:'Cinzel',serif;font-size:20pt;font-weight:700;color:${scoreCol}">${cr.score}</span></div>
    <p style="margin:0;font-size:12pt">${cr.summary}</p>
  </div>
  ${cr.blockers?.length?`<h4>Blockers — Must Resolve Before Submission</h4>${cr.blockers.map(b=>`<div class="blocker">${b}</div>`).join("")}`:""}
  ${cr.warnings?.length?`<h4>Warnings — Verify Before Submission</h4>${cr.warnings.map(w=>`<div class="warn-item">${w}</div>`).join("")}`:""}
</div>`:""}
<div class="footer">SPIRITUS FORGE ◈ LABEL GENERATOR ◈ CONFIDENTIAL — FOR LICENSED DISTILLERY USE ONLY</div>
</body></html>`);
                      win.document.close();
                    }} style={{fontSize:11}}>⬇ Export Label PDF</BtnPrimary>
                  </div>
                </div>

                {/* COLA Score Banner */}
                {labelData.colaReadiness&&(()=>{
                  const cr=labelData.colaReadiness;
                  const scoreCol=cr.score>=80?C.greenL:cr.score>=60?C.gold:C.redL;
                  return(
                    <div style={{...S.card({marginBottom:24,borderColor:`${scoreCol}44`,padding:"18px 24px"}),display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
                      <div style={{width:64,height:64,borderRadius:"50%",border:`3px solid ${scoreCol}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontFamily:F.h,fontSize:22,fontWeight:700,color:scoreCol}}>{cr.score}</span>
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontFamily:F.h,fontSize:13,color:scoreCol,marginBottom:4}}>COLA Readiness Score</div>
                        <div style={{color:C.text,fontSize:14}}>{cr.summary}</div>
                        {cr.blockers?.length>0&&<div style={{color:C.redL,fontSize:12,marginTop:6}}>⚠ {cr.blockers.length} blocker{cr.blockers.length>1?"s":""} to resolve before submission</div>}
                      </div>
                    </div>
                  );
                })()}

                {/* Design Brief */}
                <div style={S.card({marginBottom:18})}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
                    <div>
                      <h2 style={S.h2}>Designer Brief</h2>
                      <p style={{color:C.dim,fontSize:12,margin:0}}>Handoff-ready brief for your designer, Figma, or Midjourney</p>
                    </div>
                    {!labelBrief&&!labelBriefLoading&&(
                      <BtnPrimary onClick={async()=>{
                        setLabelBriefLoading(true);
                        try {
                          const hasImage = !!brandImageB64;
                          const fl = labelData.frontLabel;
                          const briefPrompt = `You are a senior beverage label designer and brand strategist. Generate a precise, actionable designer brief for this label.

PRODUCT: ${formula.productName}
TAGLINE: ${fl.tagline||"none"}
CATEGORY: ${fl.classTypeStatement||""}
ABV: ${fl.alcoholContent||""}
FLAVOR PROFILE: ${(formula.flavorNotes||[]).join(", ")}
COPY TONE: ${labelData.frontLabel?.designNotes||""}
${brandAnalysis?`
EXTRACTED BRAND IDENTITY FROM UPLOADED LABEL:
- Style: ${brandAnalysis.style}
- Typography: ${brandAnalysis.typography}
- Mood: ${brandAnalysis.mood}
- Copy tone: ${brandAnalysis.toneNotes}
- Design elements: ${brandAnalysis.designElements}
- Layout: ${JSON.stringify(brandAnalysis.layout||{})}
- Imagery: ${JSON.stringify(brandAnalysis.imagery||{})}
- Colors: ${JSON.stringify(brandAnalysis.colors||{})}
${hasImage?"The uploaded label image is also provided — study it carefully and ensure every element of this brief reflects that exact visual identity.":""}
`:"No existing label uploaded — create a brief that matches the product character."}

Generate a complete designer brief. Return ONLY valid JSON:
{
  "overview": "2-3 sentence creative direction summary",
  "labelShape": "exact shape recommendation e.g. 'tall portrait rectangle 2.5 x 4 inches' or 'oval 3 x 4 inches'",
  "colorPalette": [
    {"name":"color role e.g. Background","hex":"#hex","usage":"where and how to use this color"}
  ],
  "typography": {
    "primaryFont": "specific font name or category e.g. 'Playfair Display — elegant serif' or 'Futura PT — geometric sans'",
    "secondaryFont": "font for body/regulatory copy",
    "nameTreatment": "describe exactly how the brand name should look — size, weight, tracking, case, effects",
    "hierarchy": "describe the type size hierarchy across the label"
  },
  "layout": {
    "zones": ["describe each zone top to bottom e.g. 'Top third: crest/emblem centered'","'Center: brand name dominant'","'Lower third: regulatory copy in two columns'"],
    "namePosition": "where and how large",
    "imageryPosition": "where illustration/emblem sits",
    "textAlignment": "centered|left-aligned|mixed"
  },
  "imagery": {
    "mainElement": "describe the primary visual element in detail",
    "style": "illustration style e.g. 'fine-line engraving', 'bold woodcut', 'watercolor wash', 'geometric flat'",
    "colorTreatment": "how color is applied to imagery",
    "moodBoard": ["3-5 reference words or image descriptions a designer can search e.g. 'vintage botanical engraving','desert mesa at golden hour'"]
  },
  "finishes": ["list 2-3 print finish recommendations e.g. 'Soft-touch matte laminate on base','Spot UV gloss on brand name','Gold foil on border frame'"],
  "doNot": ["2-3 things to avoid that would break brand consistency"],
  "midjourney": "a ready-to-use Midjourney prompt to generate label concept art",
  "figmaStart": "one sentence Figma starting point recommendation"
}`;

                          const messages = hasImage ? [{
                            role:"user",
                            content:[
                              {type:"image",source:{type:"base64",media_type:brandImageType,data:brandImageB64}},
                              {type:"text",text:briefPrompt}
                            ]
                          }] : [{role:"user",content:briefPrompt}];

                          const resp = await fetch(API,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
                            model:MODEL,
                            max_tokens:2000,
                            system:"Senior beverage label designer. Return only valid JSON, no markdown.",
                            messages
                          })});
                          const data = await resp.json();
                          if(!resp.ok) throw new Error(data?.error?.message||"Brief generation failed");
                          const text=(data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("");
                          const parsed=parseJSON(text);
                          if(!parsed?.overview) throw new Error("Could not parse brief — try again.");
                          setLabelBrief(parsed);
                        } catch(e){
                          setLabelBriefErr(e.message);
                        } finally { setLabelBriefLoading(false); }
                      }} style={{fontSize:12,padding:"10px 20px"}}>
                        {brandImageB64?"✦ Generate Brief from Uploaded Label":"✦ Generate Designer Brief"}
                      </BtnPrimary>
                    )}
                  </div>
                  {labelBriefErr&&<div style={{color:C.redL,fontSize:13,padding:"10px 14px",background:`${C.red}09`,border:`1px solid ${C.red}22`,borderRadius:6,marginBottom:12}}>{labelBriefErr}</div>}
                  {labelBriefLoading&&<div style={{textAlign:"center",padding:"20px 0",color:C.gold,fontSize:13}}>⚗ Generating designer brief{brandImageB64?" — analyzing your uploaded label":""}…</div>}
                  {labelBrief&&(()=>{
                    const b=labelBrief;
                    return(
                      <div>
                        {/* Overview */}
                        <div style={{padding:"14px 16px",background:`${C.copper}0e`,border:`1px solid ${C.copper}30`,borderRadius:8,marginBottom:18}}>
                          <div style={{...S.label,marginBottom:6,color:C.copper}}>Creative Direction</div>
                          <p style={{color:C.text,fontSize:14,lineHeight:1.7,margin:0}}>{b.overview}</p>
                        </div>

                        {/* Color palette */}
                        {b.colorPalette?.length>0&&(
                          <div style={{marginBottom:18}}>
                            <div style={{...S.label,marginBottom:10}}>Color Palette</div>
                            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                              {b.colorPalette.map((c,i)=>(
                                <div key={i} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden",width:120}}>
                                  <div style={{height:52,background:c.hex}}/>
                                  <div style={{padding:"8px 10px"}}>
                                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                                      <code style={{fontSize:11,color:C.copper,fontFamily:"monospace"}}>{c.hex}</code>
                                    </div>
                                    <div style={{fontSize:11,color:C.dim,fontWeight:600}}>{c.name}</div>
                                    <div style={{fontSize:10,color:C.dim2,marginTop:2,lineHeight:1.4}}>{c.usage}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Typography + Layout side by side */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:18}}>
                          <div style={S.card()}>
                            <h3 style={S.h3}>Typography</h3>
                            {[
                              ["Primary Font",b.typography?.primaryFont],
                              ["Secondary Font",b.typography?.secondaryFont],
                              ["Name Treatment",b.typography?.nameTreatment],
                              ["Type Hierarchy",b.typography?.hierarchy],
                            ].filter(([,v])=>v).map(([label,val])=>(
                              <div key={label} style={{marginBottom:12}}>
                                <div style={{...S.label,marginBottom:3}}>{label}</div>
                                <div style={{color:C.text,fontSize:13,lineHeight:1.5}}>{val}</div>
                              </div>
                            ))}
                          </div>
                          <div style={S.card()}>
                            <h3 style={S.h3}>Layout</h3>
                            <div style={{...S.label,marginBottom:6}}>Label Shape</div>
                            <div style={{color:C.text,fontSize:13,marginBottom:12}}>{b.labelShape}</div>
                            <div style={{...S.label,marginBottom:6}}>Zone Structure</div>
                            {b.layout?.zones?.map((z,i)=>(
                              <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"flex-start"}}>
                                <span style={{color:C.copper,fontSize:11,fontFamily:F.h,minWidth:16,marginTop:1}}>{i+1}</span>
                                <span style={{color:C.text,fontSize:13,lineHeight:1.5}}>{z}</span>
                              </div>
                            ))}
                            <div style={{...S.label,marginBottom:4,marginTop:8}}>Text Alignment</div>
                            <div style={{color:C.text,fontSize:13}}>{b.layout?.textAlignment}</div>
                          </div>
                        </div>

                        {/* Imagery */}
                        {b.imagery&&(
                          <div style={{...S.card({marginBottom:18})}}>
                            <h3 style={S.h3}>Imagery Direction</h3>
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                              <div>
                                <div style={{...S.label,marginBottom:4}}>Main Visual Element</div>
                                <p style={{color:C.text,fontSize:13,lineHeight:1.6,marginBottom:12}}>{b.imagery.mainElement}</p>
                                <div style={{...S.label,marginBottom:4}}>Illustration Style</div>
                                <p style={{color:C.text,fontSize:13,marginBottom:12}}>{b.imagery.style}</p>
                                <div style={{...S.label,marginBottom:4}}>Color Treatment</div>
                                <p style={{color:C.text,fontSize:13}}>{b.imagery.colorTreatment}</p>
                              </div>
                              <div>
                                <div style={{...S.label,marginBottom:8}}>Mood Board References</div>
                                {b.imagery.moodBoard?.map((m,i)=>(
                                  <div key={i} style={{padding:"7px 12px",background:`${C.gold}0a`,border:`1px solid ${C.gold}20`,borderRadius:6,marginBottom:6,fontSize:13,color:C.text,fontStyle:"italic"}}>"{m}"</div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Midjourney prompt */}
                        {b.midjourney&&(
                          <div style={{...S.card({marginBottom:18,borderColor:`${C.gold}33`})}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                              <h3 style={{...S.h3,marginBottom:0}}>Midjourney Prompt</h3>
                              <BtnOutline onClick={()=>navigator.clipboard?.writeText(b.midjourney)} style={{fontSize:10,padding:"4px 10px"}}>Copy</BtnOutline>
                            </div>
                            <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,padding:"10px 14px",fontFamily:"monospace",fontSize:12,color:C.dim,lineHeight:1.6}}>{b.midjourney}</div>
                          </div>
                        )}

                        {/* Print finishes + do nots */}
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:18}}>
                          {b.finishes?.length>0&&(
                            <div style={S.card()}>
                              <h3 style={S.h3}>Print Finishes</h3>
                              {b.finishes.map((f,i)=>(
                                <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
                                  <span style={{color:C.copper}}>◆</span>
                                  <span style={{color:C.text,fontSize:13}}>{f}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {b.doNot?.length>0&&(
                            <div style={S.card()}>
                              <h3 style={{...S.h3,color:C.redL}}>Do Not</h3>
                              {b.doNot.map((d,i)=>(
                                <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
                                  <span style={{color:C.redL}}>✕</span>
                                  <span style={{color:C.text,fontSize:13}}>{d}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {b.figmaStart&&(
                          <p style={{color:C.dim,fontSize:12,fontStyle:"italic",borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:0}}>
                            <strong style={{color:C.dim,fontStyle:"normal"}}>Figma:</strong> {b.figmaStart}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Label Visual Preview */}
                {labelBrief&&(
                  <div style={S.card({marginBottom:18})}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
                      <div>
                        <h2 style={S.h2}>Label Visual Preview</h2>
                        <p style={{color:C.dim,fontSize:12,margin:0}}>AI-generated SVG based on the designer brief — download and open in Figma or Illustrator</p>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        {labelSvg&&(
                          <BtnOutline onClick={()=>{
                            const blob=new Blob([labelSvg],{type:"image/svg+xml"});
                            const url=URL.createObjectURL(blob);
                            const a=document.createElement("a");
                            a.href=url;
                            a.download=`${(formula.productName||"label").replace(/\s+/g,"-").toLowerCase()}-label-preview.svg`;
                            a.click(); URL.revokeObjectURL(url);
                          }} style={{fontSize:11}}>⬇ Download SVG</BtnOutline>
                        )}
                        {!labelSvgLoading&&(
                          <BtnPrimary onClick={async()=>{
                            setLabelSvgLoading(true); setLabelSvg(null);
                            try {
                              const b=labelBrief;
                              const fl=labelData.frontLabel;
                              const svgPrompt=`You are an expert SVG label designer. Create a complete, beautiful beverage label as valid SVG code.

PRODUCT: ${formula.productName}
TAGLINE: ${fl.tagline||""}
CLASS/TYPE: ${fl.classTypeStatement||""}
ABV: ${fl.alcoholContent||""}
NET CONTENTS: ${fl.netContents||"750 mL"}
BOTTLER: ${fl.bottlerStatement||""}
TASTING NOTES: ${labelData.backLabel?.tastingNotes||""}
GOVERNMENT WARNING: ${fl.governmentWarning||"GOVERNMENT WARNING: (1) According to the Surgeon General, women should not drink alcoholic beverages during pregnancy because of the risk of birth defects. (2) Consumption of alcoholic beverages impairs your ability to drive a car or operate machinery, and may cause health problems."}

DESIGNER BRIEF TO FOLLOW EXACTLY:
- Overview: ${b.overview}
- Label shape: ${b.labelShape}
- Colors: ${b.colorPalette?.map(c=>c.name+": "+c.hex).join(", ")}
- Primary font style: ${b.typography?.primaryFont}
- Name treatment: ${b.typography?.nameTreatment}
- Layout zones: ${b.layout?.zones?.join(" | ")}
- Main imagery: ${b.imagery?.mainElement}
- Illustration style: ${b.imagery?.style}
- Text alignment: ${b.layout?.textAlignment}

REQUIREMENTS:
- Output ONLY the SVG code, nothing else — no explanation, no markdown, no backticks
- Start with <svg and end with </svg>
- Use viewBox="0 0 400 600" width="400" height="600"
- Include ALL mandatory TTB label elements: brand name, class/type statement, ABV, net contents, bottler statement, government warning
- Make the brand name visually dominant
- Draw the main imagery element using SVG paths, polygons, circles — make it look like the brief describes
- Use the exact colors from the brief
- Use font-family matching the brief typography style
- Make it look like a real professional label, not a placeholder
- Government warning must be small (6-7pt equivalent) at the bottom
- The label must be complete and print-ready`;

                              const resp=await fetch(API,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
                                model:MODEL,
                                max_tokens:4000,
                                system:"Expert SVG beverage label designer. Output only valid SVG code. No markdown, no backticks, no explanation. Start your response with <svg",
                                messages:[{role:"user",content:svgPrompt}]
                              })});
                              const data=await resp.json();
                              if(!resp.ok) throw new Error(data?.error?.message||"SVG generation failed");
                              const text=(data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("").trim();
                              // Extract SVG if wrapped in anything
                              const svgStart=text.indexOf("<svg");
                              const svgEnd=text.lastIndexOf("</svg>")+6;
                              const svg=svgStart>-1&&svgEnd>6?text.slice(svgStart,svgEnd):text;
                              if(!svg.includes("<svg"))throw new Error("Could not generate SVG — try again.");
                              setLabelSvg(svg);
                            } catch(e){
                              setLabelSvgErr(e.message);
                            } finally { setLabelSvgLoading(false); }
                          }} style={{fontSize:11,padding:"9px 16px"}}>
                            {labelSvg?"↺ Regenerate Preview":"✦ Generate Visual Preview"}
                          </BtnPrimary>
                        )}
                      </div>
                    </div>
                    {labelSvgErr&&<div style={{color:C.redL,fontSize:13,padding:"10px 14px",background:`${C.red}09`,border:`1px solid ${C.red}22`,borderRadius:6,marginBottom:12}}>{labelSvgErr}</div>}
                    {labelSvgLoading&&<div style={{textAlign:"center",padding:"32px 0",color:C.gold,fontSize:13}}>⚗ Designing label — this takes ~20 seconds…</div>}
                    {labelSvg&&(
                      <div>
                        <div style={{display:"flex",justifyContent:"center",padding:"24px",background:"#0a0704",borderRadius:8,marginBottom:12}}>
                          <div style={{maxWidth:300,width:"100%"}} dangerouslySetInnerHTML={{__html:labelSvg}}/>
                        </div>
                        <p style={{color:C.dim,fontSize:11,textAlign:"center",margin:0,fontStyle:"italic"}}>
                          Vector SVG — opens in Figma, Illustrator, Affinity Designer · regenerate for variations
                        </p>
                      </div>
                    )}
                    {!labelSvg&&!labelSvgLoading&&(
                      <div style={{textAlign:"center",padding:"24px 0",color:C.dim,fontSize:13}}>
                        Click "Generate Visual Preview" to create a label design based on the brief above.
                      </div>
                    )}
                  </div>
                )}

                {/* Front & Back Label */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18,marginBottom:18}}>
                  <div style={S.card()}>
                    <h2 style={S.h2}>Front Label</h2>
                    {[
                      ["Brand Name",labelData.frontLabel?.brandName,{fontSize:20,color:C.goldL,fontFamily:F.h}],
                      ["Tagline",labelData.frontLabel?.tagline,{fontStyle:"italic",color:C.dim}],
                      ["Class & Type",labelData.frontLabel?.classTypeStatement],
                      ["Statement of Composition",labelData.frontLabel?.statementOfComposition],
                      ["Alcohol Content",labelData.frontLabel?.alcoholContent],
                      ["Net Contents",labelData.frontLabel?.netContents],
                      ["Bottler Statement",labelData.frontLabel?.bottlerStatement],
                    ].filter(([,v])=>v).map(([label,val,extra={}])=>(
                      <div key={label} style={{marginBottom:14}}>
                        <div style={{...S.label,marginBottom:3}}>{label}</div>
                        <div style={{color:C.text,fontSize:14,lineHeight:1.5,...extra}}>{val}</div>
                      </div>
                    ))}
                    <div style={{marginBottom:14}}>
                      <div style={{...S.label,marginBottom:3}}>Government Warning</div>
                      <div style={{color:C.dim,fontSize:11,lineHeight:1.5,fontStyle:"italic",background:`${C.gold}09`,border:`1px solid ${C.gold}22`,borderRadius:5,padding:"8px 10px"}}>{labelData.frontLabel?.governmentWarning}</div>
                    </div>
                    {labelData.frontLabel?.designNotes&&(
                      <div style={{background:`${C.copper}0e`,border:`1px solid ${C.copper}30`,borderRadius:6,padding:"10px 12px"}}>
                        <div style={{...S.label,marginBottom:4,color:C.copper}}>Design Direction</div>
                        <div style={{color:C.dim,fontSize:13,fontStyle:"italic",lineHeight:1.6}}>{labelData.frontLabel.designNotes}</div>
                      </div>
                    )}
                  </div>
                  <div style={S.card()}>
                    <h2 style={S.h2}>Back Label</h2>
                    {[
                      ["Tasting Notes",labelData.backLabel?.tastingNotes],
                      ["Serving Suggestion",labelData.backLabel?.servingSuggestion],
                      ["Ingredient Statement",labelData.backLabel?.ingredientStatement],
                      ["Allergen Statement",labelData.backLabel?.allergenStatement],
                      ["Recycling",labelData.backLabel?.recyclingStatement],
                      ["Barcode Note",labelData.backLabel?.barcode],
                    ].filter(([,v])=>v).map(([label,val])=>(
                      <div key={label} style={{marginBottom:14}}>
                        <div style={{...S.label,marginBottom:3}}>{label}</div>
                        <div style={{color:C.text,fontSize:14,lineHeight:1.5}}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Compliance Checklist */}
                {labelData.complianceChecklist?.length>0&&(
                  <div style={S.card({marginBottom:18})}>
                    <h2 style={S.h2}>Compliance Checklist</h2>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {labelData.complianceChecklist.map((item,i)=>{
                        const col=item.status==="required"?C.copper:item.status==="recommended"?C.gold:C.greenL;
                        return(
                          <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 12px",background:`${col}0a`,border:`1px solid ${col}25`,borderRadius:6}}>
                            <span style={{display:"inline-block",background:`${col}1a`,border:`1px solid ${col}44`,color:col,borderRadius:4,padding:"2px 8px",fontSize:10,fontFamily:F.h,letterSpacing:"0.06em",flexShrink:0,marginTop:1}}>{item.status.toUpperCase()}</span>
                            <div style={{flex:1}}>
                              <div style={{color:C.text,fontSize:13,marginBottom:2}}>{item.item}</div>
                              {item.notes&&<div style={{color:C.dim,fontSize:12}}>{item.notes}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* COLA Blockers & Warnings */}
                {(labelData.colaReadiness?.blockers?.length>0||labelData.colaReadiness?.warnings?.length>0)&&(
                  <div style={S.card()}>
                    <h2 style={S.h2}>COLA Readiness Detail</h2>
                    {labelData.colaReadiness.blockers?.length>0&&(<>
                      <h3 style={{...S.h3,color:C.redL,marginBottom:10}}>Blockers — Resolve Before Submission</h3>
                      {labelData.colaReadiness.blockers.map((b,i)=>(
                        <div key={i} style={{borderLeft:`3px solid ${C.redL}`,background:`${C.red}09`,borderRadius:"0 6px 6px 0",padding:"8px 12px",marginBottom:8,fontSize:13,color:C.text}}>{b}</div>
                      ))}
                    </>)}
                    {labelData.colaReadiness.warnings?.length>0&&(<>
                      <h3 style={{...S.h3,color:C.gold,marginBottom:10,marginTop:16}}>Warnings — Verify Before Submission</h3>
                      {labelData.colaReadiness.warnings.map((w,i)=>(
                        <div key={i} style={{borderLeft:`3px solid ${C.gold}`,background:`${C.gold}09`,borderRadius:"0 6px 6px 0",padding:"8px 12px",marginBottom:8,fontSize:13,color:C.text}}>{w}</div>
                      ))}
                    </>)}
                    <p style={{color:C.dim2,fontSize:11,fontStyle:"italic",marginTop:12,marginBottom:0}}>This AI-generated label spec is not a substitute for professional TTB/FDA compliance review. Verify with a licensed specialist before COLA submission.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </main>

      <footer style={{textAlign:"center",padding:"22px 16px",borderTop:`1px solid ${C.border}`,color:C.dim2,fontSize:11,fontFamily:F.h,letterSpacing:"0.18em"}}>FORGE PLATFORM ◈ AI FORMULATION ◈ spiritusforge.io</footer>

      <style>{`
        * { box-sizing: border-box; }
        textarea,input,select { font-family: inherit; transition: border-color 0.2s; }
        textarea:focus,input:focus,select:focus { border-color: ${C.copper} !important; box-shadow: 0 0 0 2px ${C.copper}1e; }
        select option { background: #1e1b12; }
        @keyframes loadbar { 0%{width:15%} 50%{width:85%} 100%{width:15%} }
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:${C.bg}}
        ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px}
      `}</style>
    </>)}
    </div>
  );
}
